import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Trophy, Plus, Copy, RefreshCw, AlertCircle, Loader2, CheckCircle2, X, Pencil, Trash2, Share2, BarChart3, UserCog, MessageCircle, Link as LinkIcon, Info, ClipboardList, Minus, AlertTriangle, Download, Lock, Shuffle } from 'lucide-react';
import api from '../services/api';
import { getRoundName, isKnockoutStageMatch as isKnockoutStageMatchShared } from '../utils/tournamentRoundName';
import { buildPlayerById, resolveMatchSide, resolveEntityName, isIndividualRegistration } from '../utils/tournamentPlayers';
import { forAgainstLabels } from '../utils/tournamentLabels';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from '../hooks/use-toast';

const SPORTS = [
  { v: 'padel',      label: 'Padel' },
  { v: 'pickleball', label: 'Pickleball' },
  { v: 'football',   label: 'Football' },
  { v: 'cricket',    label: 'Cricket' },
  { v: 'other',      label: 'Other' },
];

const FORMATS_BY_SPORT = {
  football:   ['knockout', 'double_elimination', 'round_robin', 'group_knockout'],
  cricket:    ['knockout', 'double_elimination', 'round_robin', 'group_knockout'],
  padel:      ['knockout', 'double_elimination', 'round_robin', 'group_knockout',
               'americano', 'mexicano', 'mixed_americano', 'king_of_court'],
  pickleball: ['knockout', 'double_elimination', 'round_robin', 'group_knockout',
               'americano', 'mexicano', 'mixed_americano', 'king_of_court'],
  other:      ['knockout', 'double_elimination', 'round_robin', 'group_knockout'],
};

const FORMAT_LABELS = {
  knockout:           'Knockout (Single Elimination)',
  double_elimination: 'Double Elimination',
  round_robin:        'Round Robin',
  group_knockout:     'Group Stage + Knockout',
  americano:          'Americano',
  mexicano:           'Mexicano',
  mixed_americano:    'Mixed Americano',
  king_of_court:      'King of the Court',
};

const INDIVIDUAL_FORMATS = new Set(['americano', 'mexicano', 'mixed_americano', 'king_of_court']);
const BRACKET_FORMATS = new Set(['knockout', 'double_elimination', 'group_knockout']);
// FIX 3: points table only applies to formats that have a points-driven standings
const FORMATS_WITH_POINTS = new Set(['round_robin', 'group_knockout']);
// FIX 2: tiebreaker only applies to formats that have a knockout stage
const FORMATS_WITH_KO_STAGE = new Set(['knockout', 'double_elimination', 'group_knockout']);

// ─────────────────────────────────────────────────────────────
// Racquet scoring config (Games → Sets → Match). Only applies to
// team-based racquet formats (padel/pickleball/tennis). Individual
// formats (americano/mexicano/king-of-court) use points_per_match
// scoring and never emit `scoring_by_stage`.
// ─────────────────────────────────────────────────────────────
const RACQUET_SPORTS = new Set(['padel', 'pickleball', 'tennis']);
const RACQUET_TEAM_FORMATS = new Set(['round_robin', 'knockout', 'double_elimination', 'group_knockout']);

const STAGE_LABELS = {
  default:   'Default (all other rounds)',
  group:     'Group stage',
  r16:       'Round of 16',
  quarters:  'Quarter-finals',
  semis:     'Semi-finals',
  final:     'Final',
};

// Which stages to expose per format. `default` is always first —
// diverging stages are opt-in via a "same as default" toggle.
const stageKeysForFormat = (fmt) => {
  if (fmt === 'round_robin') return ['default'];
  if (fmt === 'group_knockout') return ['default', 'group', 'r16', 'quarters', 'semis', 'final'];
  if (fmt === 'knockout' || fmt === 'double_elimination') return ['default', 'quarters', 'semis', 'final'];
  return ['default'];
};

// Sensible padel/pickleball defaults per stage — matches the payload
// the wizard used to hardcode. Keeps behaviour identical for users
// who don't touch the config.
const defaultScoringByStage = (fmt) => {
  const base = { mode: 'games_sets_match', games_target: 6, win_by_margin: 1,
                 tiebreak_at: 6, tiebreak_target: 7, tiebreak_win_by: 2 };
  const map = { default: { ...base, best_of_sets: 3 } };
  if (fmt === 'group_knockout') {
    map.group    = { ...base, best_of_sets: 1 };
    map.r16      = { ...base, best_of_sets: 3 };
    map.quarters = { ...base, best_of_sets: 3 };
    map.semis    = { ...base, best_of_sets: 3 };
    map.final    = { ...base, best_of_sets: 5 };
  } else if (fmt === 'knockout' || fmt === 'double_elimination') {
    map.quarters = { ...base, best_of_sets: 3 };
    map.semis    = { ...base, best_of_sets: 3 };
    map.final    = { ...base, best_of_sets: 5 };
  }
  return map;
};

// Validate a single stage's rules. Returns null if OK, else a
// human-readable error string. Called on the editor's onSave and
// before wizard submit.
const validateStageCfg = (cfg) => {
  const bo = parseInt(cfg.best_of_sets, 10);
  if (![1, 3, 5, 7, 9].includes(bo)) return 'Best-of must be 1, 3, 5, 7, or 9.';
  const gt = parseInt(cfg.games_target, 10);
  if (!(gt >= 1 && gt <= 30)) return 'Games to win a set must be 1–30.';
  if (cfg.tiebreak_at != null && cfg.tiebreak_at !== '') {
    const ta = parseInt(cfg.tiebreak_at, 10);
    if (!(ta >= 1 && ta <= gt)) return `Tiebreak-at must be between 1 and ${gt}.`;
    const tt = parseInt(cfg.tiebreak_target, 10);
    if (!(tt >= 1 && tt <= 99)) return 'Tiebreak first-to must be 1–99.';
    const tw = parseInt(cfg.tiebreak_win_by, 10);
    if (!(tw >= 1 && tw <= 9)) return 'Tiebreak win-by must be 1–9.';
  }
  return null;
};

// Normalize a stage row from the editor UI back to backend shape:
// tiebreak fields are `null` when the toggle is off, and everything
// is coerced to int.
const normalizeStageCfg = (cfg) => {
  const out = {
    mode: 'games_sets_match',
    best_of_sets: parseInt(cfg.best_of_sets, 10),
    games_target: parseInt(cfg.games_target, 10),
    win_by_margin: parseInt(cfg.win_by_margin, 10) || 1,
  };
  if (cfg.tiebreak_at != null && cfg.tiebreak_at !== '') {
    out.tiebreak_at = parseInt(cfg.tiebreak_at, 10);
    out.tiebreak_target = parseInt(cfg.tiebreak_target, 10);
    out.tiebreak_win_by = parseInt(cfg.tiebreak_win_by, 10);
  } else {
    out.tiebreak_at = null;
    out.tiebreak_target = null;
    out.tiebreak_win_by = null;
  }
  return out;
};

// Per-stage editor row. Handles the "same as default" toggle for
// non-default stages by dropping/re-adding the stage entry.
const StageRow = ({ stageKey, cfg, defaultCfg, onChange, disabled }) => {
  const isDefault = stageKey === 'default';
  const following = !isDefault && (cfg == null);
  const effective = following ? defaultCfg : cfg;
  const [err, setErr] = useState('');

  const setField = (k, v) => {
    const next = { ...(effective || defaultCfg), [k]: v };
    // Coerce tiebreak_at empty → disable tiebreak entirely.
    if (k === 'tiebreak_at' && (v === '' || v === null)) {
      next.tiebreak_at = null;
      next.tiebreak_target = null;
      next.tiebreak_win_by = null;
    }
    // When enabling tiebreak from empty, seed sensible defaults.
    if (k === 'tiebreak_at' && v !== '' && v !== null && (effective?.tiebreak_target == null)) {
      next.tiebreak_target = 7;
      next.tiebreak_win_by = 2;
    }
    const msg = validateStageCfg(next);
    setErr(msg || '');
    onChange(next);
  };

  const tbEnabled = effective?.tiebreak_at != null && effective?.tiebreak_at !== '';
  const boOptions = [1, 3, 5, 7, 9];

  return (
    <div className={`border rounded-lg p-3 ${following ? 'bg-gray-50' : 'bg-white'}`}
         data-testid={`stage-row-${stageKey}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold text-gray-900">{STAGE_LABELS[stageKey] || stageKey}</div>
        {!isDefault && (
          <label className="flex items-center gap-2 text-xs text-gray-600 select-none"
                 data-testid={`stage-follow-default-${stageKey}`}>
            <input type="checkbox" checked={following} disabled={disabled}
              onChange={(e) => onChange(e.target.checked ? null : { ...defaultCfg })} />
            <span>Same as default</span>
          </label>
        )}
      </div>
      {!following && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <label className="text-xs text-gray-600">
              Best of
              <select value={effective.best_of_sets} disabled={disabled}
                onChange={(e) => setField('best_of_sets', e.target.value)}
                className="mt-0.5 w-full px-2 py-1.5 border rounded text-sm"
                data-testid={`stage-${stageKey}-best-of`}>
                {boOptions.map(n => <option key={n} value={n}>{n} set{n === 1 ? '' : 's'}</option>)}
              </select>
            </label>
            <label className="text-xs text-gray-600">
              Games to win
              <Input type="number" min="1" max="30" value={effective.games_target} disabled={disabled}
                onChange={(e) => setField('games_target', e.target.value)}
                className="mt-0.5 w-full px-2 py-1.5 text-sm"
                data-testid={`stage-${stageKey}-games-target`} />
            </label>
            <label className="text-xs text-gray-600">
              Tiebreak
              <select value={tbEnabled ? 'on' : 'off'} disabled={disabled}
                onChange={(e) => setField('tiebreak_at', e.target.value === 'on' ? (parseInt(effective.games_target, 10) || 6) : '')}
                className="mt-0.5 w-full px-2 py-1.5 border rounded text-sm"
                data-testid={`stage-${stageKey}-tiebreak-toggle`}>
                <option value="on">On</option>
                <option value="off">Off</option>
              </select>
            </label>
            <label className="text-xs text-gray-600">
              At games
              <Input type="number" min="1" max={parseInt(effective.games_target, 10) || 30}
                value={tbEnabled ? effective.tiebreak_at : ''}
                disabled={disabled || !tbEnabled}
                onChange={(e) => setField('tiebreak_at', e.target.value)}
                className="mt-0.5 w-full px-2 py-1.5 text-sm"
                data-testid={`stage-${stageKey}-tiebreak-at`} />
            </label>
          </div>
          {tbEnabled && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
              <label className="text-xs text-gray-600">
                Tiebreak first-to
                <Input type="number" min="1" max="99" value={effective.tiebreak_target ?? ''} disabled={disabled}
                  onChange={(e) => setField('tiebreak_target', e.target.value)}
                  className="mt-0.5 w-full px-2 py-1.5 text-sm"
                  data-testid={`stage-${stageKey}-tb-target`} />
              </label>
              <label className="text-xs text-gray-600">
                Tiebreak win-by
                <Input type="number" min="1" max="9" value={effective.tiebreak_win_by ?? ''} disabled={disabled}
                  onChange={(e) => setField('tiebreak_win_by', e.target.value)}
                  className="mt-0.5 w-full px-2 py-1.5 text-sm"
                  data-testid={`stage-${stageKey}-tb-winby`} />
              </label>
              <div className="sm:col-span-2 text-[11px] text-gray-500 self-end">
                At {effective.tiebreak_at}-{effective.tiebreak_at}, first to {effective.tiebreak_target || '?'} (win by {effective.tiebreak_win_by || '?'}). Set is recorded as {(parseInt(effective.tiebreak_at,10)||0)+1}-{effective.tiebreak_at}.
              </div>
            </div>
          )}
          {err && (
            <div className="mt-2 text-xs text-red-600" data-testid={`stage-${stageKey}-error`}>{err}</div>
          )}
        </>
      )}
    </div>
  );
};

// Full editor: takes a scoring_by_stage object + format, renders one
// StageRow per applicable stage. Emits the full updated object via
// onChange. `disabled` freezes the whole UI (used post-start).
const RacquetScoringEditor = ({ value, format, onChange, disabled = false }) => {
  const stages = stageKeysForFormat(format);
  const defaultCfg = value?.default || defaultScoringByStage(format).default;

  const updateStage = (key, cfgOrNull) => {
    const next = { ...(value || {}) };
    if (cfgOrNull == null && key !== 'default') {
      delete next[key];
    } else {
      next[key] = cfgOrNull;
    }
    onChange(next);
  };

  return (
    <div className="space-y-2" data-testid="racquet-scoring-editor">
      <div className="text-sm font-semibold text-gray-900">Scoring per stage</div>
      <div className="text-xs text-gray-500 -mt-1">
        Configure best-of sets, games-per-set, and tiebreak rules. Non-default stages fall back to <strong>Default</strong> until you diverge.
      </div>
      {stages.map(k => (
        <StageRow key={k} stageKey={k} cfg={value?.[k]} defaultCfg={defaultCfg}
          onChange={(c) => updateStage(k, c)} disabled={disabled} />
      ))}
    </div>
  );
};

// Validate an entire scoring_by_stage object. Returns [] if OK, else
// list of "<stage>: <msg>" strings.
const validateScoringByStage = (obj) => {
  if (!obj) return [];
  const errs = [];
  for (const [key, cfg] of Object.entries(obj)) {
    if (cfg == null) continue;
    const msg = validateStageCfg(cfg);
    if (msg) errs.push(`${STAGE_LABELS[key] || key}: ${msg}`);
  }
  return errs;
};


// FIX 2: Knockout-stage discriminator is exported from
// utils/tournamentRoundName so admin / referee / public stay in sync.


// ─────────────────────────────────────────────────────────────
// Top-level router
// ─────────────────────────────────────────────────────────────
const TournamentManagement = () => (
  <Routes>
    <Route index element={<TournamentList />} />
    <Route path="new" element={<CreateTournamentWizard />} />
    <Route path=":id" element={<TournamentDashboard />} />
  </Routes>
);
export default TournamentManagement;


// ─────────────────────────────────────────────────────────────
// List view
// ─────────────────────────────────────────────────────────────
const TournamentList = () => {
  const nav = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.request('/tournaments/admin');
      setTournaments(data || []);
    } catch (e) {
      toast({ title: 'Failed to load tournaments', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = tournaments.filter(t => filter === 'all' || t.status === filter);

  return (
    <div className="p-4 lg:p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Tournaments</h1>
          <p className="text-sm text-gray-500">Knockout · Round Robin · Americano · Mexicano · King of the Court</p>
        </div>
        <Button onClick={() => nav('/admin/tournaments/new')} data-testid="create-tournament-btn">
          <Plus className="h-4 w-4 mr-2" /> New Tournament
        </Button>
      </div>

      <div className="flex gap-2 mb-4">
        {['all', 'setup', 'active', 'completed', 'cancelled'].map(s => (
          <button key={s}
            onClick={() => setFilter(s)}
            className={`text-xs px-3 py-1.5 rounded-full font-medium ${filter === s ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            data-testid={`filter-${s}`}
          >{s[0].toUpperCase() + s.slice(1)}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500 bg-white rounded-2xl border" data-testid="empty-state">
          <Trophy className="h-10 w-10 mx-auto text-gray-300 mb-2" />
          <p className="text-sm">No tournaments yet. Click "New Tournament" to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map(t => (
            <button key={t.id}
              onClick={() => nav(`/admin/tournaments/${t.id}`)}
              className="text-left bg-white border rounded-2xl p-4 hover:border-blue-400 hover:shadow-sm transition"
              data-testid={`tournament-card-${t.id}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="font-semibold text-gray-900">{t.title}</div>
                <StatusPill status={t.status} />
              </div>
              <div className="text-xs text-gray-500 flex flex-wrap gap-x-3 gap-y-1">
                <span>{FORMAT_LABELS[t.format] || t.format}</span>
                <span className="capitalize">{t.sport}</span>
                <span>{t.num_courts} court{t.num_courts !== 1 ? 's' : ''}</span>
              </div>
              {t.status === 'active' && (
                <div className="text-xs text-blue-700 mt-2 font-medium">
                  Round {t.current_round || 1} of {t.total_rounds || '?'}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    setup:        ['bg-gray-100 text-gray-700',  'Setup'],
    registration: ['bg-amber-100 text-amber-700', 'Registration'],
    active:       ['bg-blue-100 text-blue-700',   'Active'],
    completed:    ['bg-emerald-100 text-emerald-700', 'Completed'],
    cancelled:    ['bg-red-100 text-red-700',     'Cancelled'],
  };
  const [cls, label] = map[status] || ['bg-gray-100 text-gray-700', status];
  return <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
};


// ─────────────────────────────────────────────────────────────
// 5-Step Create Wizard
// ─────────────────────────────────────────────────────────────
const STEP_LABELS = ['Basics', 'Format', 'Teams', 'Schedule', 'Review'];

const CreateTournamentWizard = () => {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [events, setEvents] = useState([]);
  const [activities, setActivities] = useState([]);
  const [form, setForm] = useState({
    title: '',
    sport: 'padel',
    format: 'knockout',
    num_courts: 2,
    source: 'standalone',
    event_id: '',
    activity_id: '',
    group_size: 4,
    group_size_choice: '4',
    group_assignment_mode: 'auto',
    qual_direct: 1,
    qual_wildcards: 0,
    teams_advancing_per_group: 2,
    points_per_match: 24,
    king_rotation_rule: 'winners_stay_losers_rotate_down',
    match_format: 'T10',
    custom_overs: 10,
    cricket_tiebreaker: 'none',
    points_win: 3, points_draw: 1, points_loss: 0,
    football_tiebreaker: 'none',
    dupr_enabled: false,
    total_rounds: 8,
    match_date: '',
    start_time: '',
    minutes_per_match: 30,
    scoring_by_stage: defaultScoringByStage('knockout'),
  });
  const [teams, setTeams] = useState([]);  // [{team_name, players:[{name, phone, gender, dupr_rating}]}]
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // King of the Court needs ≥2 courts. Used to block the wizard at create time
  // (not just at backend start). Drives the inline error, the Next-button block
  // at Step 2, AND the final Create-button/submit guard.
  const kotcCourtsInvalid =
    form.format === 'king_of_court' && (parseInt(form.num_courts, 10) || 0) < 2;

  const availableFormats = FORMATS_BY_SPORT[form.sport] || [];

  // Load events when source becomes linked
  useEffect(() => {
    if (form.source !== 'linked') return;
    api.request('/events').then(setEvents).catch(() => setEvents([]));
  }, [form.source]);

  // Load activities for the selected event
  useEffect(() => {
    if (!form.event_id) { setActivities([]); return; }
    api.request(`/events/${form.event_id}`)
      .then(data => setActivities(data.activities || []))
      .catch(() => setActivities([]));
  }, [form.event_id]);

  // Pre-fill sport/format from selected activity
  useEffect(() => {
    const act = activities.find(a => a.id === form.activity_id);
    if (!act) return;
    setForm(p => ({
      ...p,
      sport: act.sport || p.sport,
      format: act.tournament_format || p.format,
    }));
  }, [form.activity_id, activities]);

  // Reset format if sport change makes it invalid
  useEffect(() => {
    if (!availableFormats.includes(form.format)) {
      setForm(p => ({ ...p, format: availableFormats[0] }));
    }
    if (form.sport !== 'pickleball' && form.dupr_enabled) {
      setForm(p => ({ ...p, dupr_enabled: false }));
    }
    if (form.sport !== 'pickleball' && form.sport !== 'padel' && INDIVIDUAL_FORMATS.has(form.format)) {
      setForm(p => ({ ...p, format: 'knockout' }));
    }
  }, [form.sport]); // eslint-disable-line

  // Re-seed scoring_by_stage when the format changes so stage keys
  // match the new bracket shape (e.g. `group` only exists for
  // group_knockout). Only affects racquet team formats.
  useEffect(() => {
    if (!RACQUET_SPORTS.has(form.sport)) return;
    if (!RACQUET_TEAM_FORMATS.has(form.format)) return;
    const expected = stageKeysForFormat(form.format);
    const current = form.scoring_by_stage || {};
    // Keep any existing stage values that still apply; drop stages no
    // longer relevant; add defaults for newly-relevant stages.
    const fresh = defaultScoringByStage(form.format);
    const next = {};
    for (const k of expected) {
      // Preserve user edits when possible.
      if (current[k]) next[k] = current[k];
      else if (k === 'default') next[k] = fresh.default;
      // Non-default stages default to "same as default" (absent).
    }
    // Only update if shape actually changed (avoid infinite loop).
    const same = Object.keys(current).length === Object.keys(next).length
      && Object.keys(current).every(k => next[k] !== undefined);
    if (!same) setForm(p => ({ ...p, scoring_by_stage: next }));
  }, [form.sport, form.format]); // eslint-disable-line

  // FIX ④: Tiebreaker is REQUIRED for any KO-stage format. If the user
  // selected such a format, ensure the tiebreaker default is set to a
  // non-"none" value so the submission carries a real tiebreaker.
  useEffect(() => {
    if (!FORMATS_WITH_KO_STAGE.has(form.format)) return;
    if (form.sport === 'football' && form.football_tiebreaker === 'none') {
      setForm(p => ({ ...p, football_tiebreaker: 'penalty_shootout' }));
    }
    if (form.sport === 'cricket' && form.cricket_tiebreaker === 'none') {
      setForm(p => ({ ...p, cricket_tiebreaker: 'super_over' }));
    }
  }, [form.format, form.sport]); // eslint-disable-line

  const canNext = () => {
    if (step === 1) {
      if (!form.title.trim() || !form.sport || !(form.num_courts > 0)) return false;
      if (form.source === 'linked' && (!form.event_id || !form.activity_id)) return false;
      return true;
    }
    if (step === 2) {
      if (!form.format) return false;
      if (form.format === 'king_of_court' && (parseInt(form.num_courts, 10) || 0) < 2) return false;
      return true;
    }
    if (step === 3) {
      if (form.source === 'linked') return true; // teams imported post-creation
      return teams.length >= minTeams(form.format);
    }
    if (step === 4) return true;
    return true;
  };

  const submit = async () => {
    if (kotcCourtsInvalid) {
      toast({ title: 'King of the Court needs at least 2 courts',
        description: 'Go back to Step 1 and set "Number of courts" to 2 or more.',
        variant: 'destructive' });
      setStep(2);
      return;
    }
    setSubmitting(true);
    try {
      const me = await api.request('/auth/me');
      const payload = {
        title: form.title.trim(),
        sport: form.sport,
        venue_id: me.facility_id,
        format: form.format,
        num_courts: parseInt(form.num_courts, 10),
        source: form.source,
      };
      if (form.match_date) payload.match_date = form.match_date;
      if (form.start_time) payload.start_time = form.start_time;
      if (form.minutes_per_match) payload.minutes_per_match = parseInt(form.minutes_per_match, 10);
      if (form.format === 'group_knockout') {
        payload.group_size = parseInt(form.group_size, 10);
        payload.teams_advancing_per_group = parseInt(form.teams_advancing_per_group, 10);
        payload.group_assignment_mode = form.group_assignment_mode || 'auto';
        const direct = parseInt(form.qual_direct, 10) || 1;
        const wildcards = parseInt(form.qual_wildcards, 10) || 0;
        payload.qualification = { direct_per_group: direct, wildcards };
      }
      if (['americano', 'mexicano', 'mixed_americano'].includes(form.format)) {
        payload.points_per_match = parseInt(form.points_per_match, 10);
      }
      if (INDIVIDUAL_FORMATS.has(form.format)) {
        payload.total_rounds = parseInt(form.total_rounds, 10);
      }
      if (form.format === 'king_of_court') payload.king_rotation_rule = form.king_rotation_rule;
      if (form.sport === 'cricket') {
        payload.match_format = form.match_format;
        if (form.match_format === 'custom') payload.custom_overs = parseInt(form.custom_overs, 10);
        if (FORMATS_WITH_KO_STAGE.has(form.format)) {
          payload.cricket_tiebreaker = form.cricket_tiebreaker;
        }
      }
      if (form.sport === 'football') {
        if (FORMATS_WITH_POINTS.has(form.format)) {
          payload.points_win = parseInt(form.points_win, 10);
          payload.points_draw = parseInt(form.points_draw, 10);
          payload.points_loss = parseInt(form.points_loss, 10);
        }
        if (FORMATS_WITH_KO_STAGE.has(form.format)) {
          payload.football_tiebreaker = form.football_tiebreaker;
        }
      }
      if (form.sport === 'pickleball') payload.dupr_enabled = !!form.dupr_enabled;
      // Racquet-sport scoring config: only for TEAM-based formats
      // (round_robin / knockout / double_elimination / group_knockout).
      // Individual formats (americano/mexicano/king_of_court) use
      // points_per_match and don't need this. Values come from the
      // form state so per-stage overrides set in Step 2 are applied.
      if (RACQUET_SPORTS.has(form.sport) && RACQUET_TEAM_FORMATS.has(form.format)) {
        const raw = form.scoring_by_stage || defaultScoringByStage(form.format);
        const errs = validateScoringByStage(raw);
        if (errs.length) {
          toast({ title: 'Fix scoring per stage', description: errs[0], variant: 'destructive' });
          setStep(2);
          setSubmitting(false);
          return;
        }
        const cleaned = {};
        for (const [k, v] of Object.entries(raw)) {
          if (v == null) continue;  // "same as default" — omit
          cleaned[k] = normalizeStageCfg(v);
        }
        payload.scoring_by_stage = cleaned;
      }
      if (form.source === 'linked') {
        payload.event_id = form.event_id || null;
        payload.activity_id = form.activity_id || null;
      }

      const created = await api.request('/tournaments/admin',
        { method: 'POST', body: JSON.stringify(payload) });
      // Add manually-entered teams (linked tournaments rely on auto-import; the
      // backend has already pulled paid registrations as teams).
      if (form.source !== 'linked') {
        for (const t of teams) {
        await api.request(`/tournaments/admin/${created.id}/teams`, {
          method: 'POST',
          body: JSON.stringify({
            team_name: t.team_name.trim(),
            captain_name: t.players[0]?.name || null,
            players: t.players.map(p => ({
              name: p.name, phone: p.phone || null,
              gender: p.gender || null,
              dupr_rating: p.dupr_rating ? parseFloat(p.dupr_rating) : null,
            })),
          }),
        });
        }
      }
      toast({ title: 'Tournament created', description: `Referee PIN: ${created.referee_pin}` });
      nav(`/admin/tournaments/${created.id}`, { state: { initialPin: created.referee_pin } });
    } catch (e) {
      toast({ title: 'Could not create tournament', description: e.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 lg:p-6 pb-28 lg:pb-6 max-w-3xl mx-auto" data-testid="create-tournament-wizard">
      <button onClick={() => nav('/admin/tournaments')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4" data-testid="back-to-tournaments">
        <ArrowLeft className="h-4 w-4" /> Back to Tournaments
      </button>
      <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">Create Tournament</h1>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-6 text-xs overflow-x-auto">
        {STEP_LABELS.map((label, idx) => {
          const n = idx + 1;
          return (
            <React.Fragment key={n}>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full whitespace-nowrap ${step === n ? 'bg-gray-900 text-white' : step > n ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                <span className="font-semibold">Step {n}</span>
                <span>{label}</span>
              </div>
              {n < STEP_LABELS.length && <ChevronRight className="h-4 w-4 text-gray-300 shrink-0" />}
            </React.Fragment>
          );
        })}
      </div>

      {step === 1 && <Step1Basics form={form} set={set} events={events} activities={activities} />}
      {step === 2 && <Step2Format form={form} set={set} availableFormats={availableFormats} teams={teams} />}
      {step === 3 && <Step3Teams form={form} teams={teams} setTeams={setTeams} />}
      {step === 4 && <Step4Schedule form={form} set={set} />}
      {step === 5 && <Step5Review form={form} teams={teams} />}

      {kotcCourtsInvalid && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-testid="kotc-courts-error-wizard">
          King of the Court needs at least <strong>2 courts</strong>. Go back to Step 1 and set "Number of courts" to 2 or more before creating this tournament.
        </div>
      )}

      <div className="flex gap-2 justify-between mt-6">
        <Button variant="outline" disabled={step === 1 || submitting}
          onClick={() => setStep(s => s - 1)} data-testid="wizard-back-btn">Back</Button>
        {step < 5 ? (
          <Button onClick={() => setStep(s => s + 1)} disabled={!canNext()} data-testid={`wizard-step${step}-next-btn`}>
            Next
          </Button>
        ) : (
          <Button onClick={submit} disabled={submitting || kotcCourtsInvalid} data-testid="wizard-create-btn">
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create Tournament
          </Button>
        )}
      </div>
    </div>
  );
};

const minTeams = (fmt) => {
  if (fmt === 'double_elimination') return 4;
  if (INDIVIDUAL_FORMATS.has(fmt)) return 4;
  if (fmt === 'king_of_court') return 8;
  return 2;
};

// Step 1: Basics
const Step1Basics = ({ form, set, events = [], activities = [] }) => (
  <div className="bg-white border rounded-2xl p-5 space-y-4" data-testid="step-1">
    <Field label="Tournament title *" testid="field-title">
      <input type="text" value={form.title}
        onChange={e => set('title', e.target.value)}
        placeholder="e.g. Summer Padel Open 2026"
        className="w-full px-3 py-2 border rounded-lg text-sm"
        data-testid="input-title" />
    </Field>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Sport *">
        <select value={form.sport} onChange={e => set('sport', e.target.value)}
          className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="input-sport">
          {SPORTS.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
      </Field>
      <Field label="Number of courts *">
        <input type="number" min="1" max="64" value={form.num_courts}
          onChange={e => set('num_courts', e.target.value)}
          className="w-full px-3 py-2 border rounded-lg text-sm"
          data-testid="input-num-courts" />
      </Field>
    </div>
    <Field label="Source">
      <div className="flex gap-2">
        {[
          { v: 'standalone', label: 'Standalone (manual teams)' },
          { v: 'linked',     label: 'Linked to an Event activity' },
        ].map(o => (
          <button key={o.v} type="button"
            onClick={() => set('source', o.v)}
            className={`flex-1 text-xs px-3 py-2 rounded-lg border ${form.source === o.v ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 text-gray-700 hover:border-gray-400'}`}
            data-testid={`input-source-${o.v}`}>{o.label}</button>
        ))}
      </div>
    </Field>
    {form.source === 'linked' && (
      <div className="space-y-3 pt-2 border-t">
        <Field label="Event *">
          <select value={form.event_id}
            onChange={e => { set('event_id', e.target.value); set('activity_id', ''); }}
            className="w-full px-3 py-2 border rounded-lg text-sm"
            data-testid="input-event-id">
            <option value="">— Select an event —</option>
            {events.map(ev => (
              <option key={ev.id} value={ev.id}>{ev.title}</option>
            ))}
          </select>
        </Field>
        <Field label="Activity *">
          <select value={form.activity_id}
            onChange={e => set('activity_id', e.target.value)}
            disabled={!form.event_id || activities.length === 0}
            className="w-full px-3 py-2 border rounded-lg text-sm disabled:bg-gray-50 disabled:text-gray-400"
            data-testid="input-activity-id">
            <option value="">{form.event_id ? '— Select an activity —' : 'Pick an event first'}</option>
            {activities.map(a => (
              <option key={a.id} value={a.id}>{a.title || a.format || a.id}</option>
            ))}
          </select>
        </Field>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
          Paid registrations from this activity will be auto-imported as teams immediately after creation. You can refresh the import later from the dashboard for late registrants.
        </div>
      </div>
    )}
  </div>
);

// Step 2: Format-specific settings
const Step2Format = ({ form, set, availableFormats, teams = [] }) => (
  <div className="bg-white border rounded-2xl p-5 space-y-4" data-testid="step-2">
    <Field label="Format *">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {availableFormats.map(f => (
          <button key={f} type="button" onClick={() => set('format', f)}
            className={`text-left text-sm px-3 py-2 rounded-lg border ${form.format === f ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 hover:border-gray-400'}`}
            data-testid={`input-format-${f}`}>
            {FORMAT_LABELS[f]}
          </button>
        ))}
      </div>
    </Field>

    {/* Group + Knockout settings */}
    {form.format === 'group_knockout' && (
      <div className="pt-2 border-t space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Group size">
            <select value={form.group_size_choice ?? String(form.group_size)}
              onChange={e => {
                const v = e.target.value;
                if (v === 'custom') { set('group_size_choice', 'custom'); }
                else { set('group_size_choice', v); set('group_size', v); }
              }}
              className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="input-group-size">
              <option value="3">3 teams per group</option>
              <option value="4">4 teams per group</option>
              <option value="5">5 teams per group</option>
              <option value="6">6 teams per group</option>
              <option value="custom">Custom…</option>
            </select>
          </Field>
          <Field label="Teams advancing per group">
            <select value={form.teams_advancing_per_group}
              onChange={e => set('teams_advancing_per_group', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              data-testid="input-advancing">
              <option value="1">Top 1</option>
              <option value="2">Top 2</option>
            </select>
          </Field>
        </div>
        {form.group_size_choice === 'custom' && (
          <Field label="Custom group size (teams per group)">
            <input type="number" min="2" value={form.group_size}
              onChange={e => set('group_size', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="input-group-size-custom" />
          </Field>
        )}
        <Field label="Assign teams to groups">
          <div className="grid grid-cols-2 gap-2">
            {['auto', 'manual'].map(m => (
              <button key={m} type="button"
                onClick={() => set('group_assignment_mode', m)}
                className={`px-3 py-2 border-2 rounded-lg text-sm font-medium ${form.group_assignment_mode === m ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                data-testid={`group-mode-${m}`}>
                {m === 'auto' ? 'Automatic' : 'Manual'}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {form.group_assignment_mode === 'manual'
              ? 'You will place each team into a group on the tournament dashboard after adding teams.'
              : 'Teams are auto-distributed across groups when you start the tournament.'}
          </p>
        </Field>

        {/* Flexible qualification (Phase 2) */}
        <div className="pt-2 border-t space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Qualify directly per group">
              <select value={form.qual_direct} onChange={e => set('qual_direct', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="input-qual-direct">
                <option value="1">Top 1 (group winner)</option>
                <option value="2">Top 2</option>
                <option value="3">Top 3</option>
              </select>
            </Field>
            <Field label="Best runners-up (wildcards)">
              <input type="number" min="0" value={form.qual_wildcards}
                onChange={e => set('qual_wildcards', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="input-qual-wildcards" />
            </Field>
          </div>
          <QualBracketPreview
            teamCount={teams.length}
            groupSize={parseInt(form.group_size, 10) || 4}
            direct={parseInt(form.qual_direct, 10) || 1}
            wildcards={parseInt(form.qual_wildcards, 10) || 0}
          />
        </div>
      </div>
    )}

    {/* Americano / Mexicano / Mixed Americano */}
    {['americano', 'mexicano', 'mixed_americano'].includes(form.format) && (
      <div className="grid grid-cols-2 gap-3 pt-2 border-t">
        <Field label="Points per match">
          <select value={form.points_per_match} onChange={e => set('points_per_match', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="input-points-per-match">
            <option value="16">16</option>
            <option value="24">24</option>
            <option value="32">32</option>
          </select>
        </Field>
        <Field label="Total rounds">
          <input type="number" min="1" max="64" value={form.total_rounds}
            onChange={e => set('total_rounds', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg text-sm"
            data-testid="input-total-rounds" />
        </Field>
      </div>
    )}

    {/* King of the Court */}
    {form.format === 'king_of_court' && (
      <div className="grid grid-cols-2 gap-3 pt-2 border-t">
        <Field label="Total rounds">
          <input type="number" min="1" max="64" value={form.total_rounds}
            onChange={e => set('total_rounds', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg text-sm"
            data-testid="input-total-rounds" />
        </Field>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
          Winners stay on Court 1 · Court-N losers wait at least one round before re-entering.
        </div>
      </div>
    )}

    {/* King of the Court requires ≥2 courts — block Next with a clear error */}
    {form.format === 'king_of_court' && (parseInt(form.num_courts, 10) || 0) < 2 && (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700" data-testid="kotc-courts-error">
        King of the Court needs at least <strong>2 courts</strong>. Go back to Step 1 and set "Number of courts" to 2 or more before continuing.
      </div>
    )}

    {/* Football specifics — Points only for RR / Group+KO; Tiebreaker only for KO-stage formats */}
    {form.sport === 'football' && (FORMATS_WITH_POINTS.has(form.format) || FORMATS_WITH_KO_STAGE.has(form.format)) && (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 pt-2 border-t">
        {FORMATS_WITH_POINTS.has(form.format) && (
          <>
            <Field label="Points: Win"><input type="number" min="0" value={form.points_win} onChange={e => set('points_win', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="input-points-win" /></Field>
            <Field label="Points: Draw"><input type="number" min="0" value={form.points_draw} onChange={e => set('points_draw', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="input-points-draw" /></Field>
            <Field label="Points: Loss"><input type="number" min="0" value={form.points_loss} onChange={e => set('points_loss', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="input-points-loss" /></Field>
          </>
        )}
        {FORMATS_WITH_KO_STAGE.has(form.format) && (
          <Field label={form.format === 'group_knockout' ? 'Tiebreaker (KO stage only) *' : 'Tiebreaker *'}>
            <select value={form.football_tiebreaker === 'none' ? 'penalty_shootout' : form.football_tiebreaker}
              onChange={e => set('football_tiebreaker', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="input-football-tiebreaker">
              <option value="penalty_shootout">Penalty Shootout</option>
            </select>
          </Field>
        )}
      </div>
    )}

    {/* Cricket specifics */}
    {form.sport === 'cricket' && (
      <div className="grid grid-cols-2 gap-3 pt-2 border-t">
        <Field label="Overs format">
          <select value={form.match_format} onChange={e => set('match_format', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="input-match-format">
            <option value="T5">T5</option><option value="T10">T10</option>
            <option value="T20">T20</option><option value="custom">Custom</option>
          </select>
        </Field>
        {form.match_format === 'custom' && (
          <Field label="Custom overs">
            <input type="number" min="1" max="50" value={form.custom_overs}
              onChange={e => set('custom_overs', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="input-custom-overs" />
          </Field>
        )}
        {FORMATS_WITH_KO_STAGE.has(form.format) && (
          <Field label={form.format === 'group_knockout' ? 'Tiebreaker (KO stage only) *' : 'Tiebreaker *'}>
            <select value={form.cricket_tiebreaker === 'none' ? 'super_over' : form.cricket_tiebreaker}
              onChange={e => set('cricket_tiebreaker', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="input-cricket-tiebreaker">
              <option value="super_over">Super Over</option>
            </select>
          </Field>
        )}
      </div>
    )}

    {/* Racquet scoring per stage — padel/pickleball/tennis + team-based formats */}
    {RACQUET_SPORTS.has(form.sport) && RACQUET_TEAM_FORMATS.has(form.format) && (
      <div className="pt-2 border-t">
        <RacquetScoringEditor
          value={form.scoring_by_stage}
          format={form.format}
          onChange={(v) => set('scoring_by_stage', v)}
        />
      </div>
    )}

    {/* Pickleball DUPR toggle */}
    {form.sport === 'pickleball' && (
      <div className="flex items-center justify-between pt-2 border-t">
        <div>
          <div className="text-sm font-semibold">Enable DUPR seeding</div>
          <div className="text-xs text-gray-500">Seed teams by player DUPR ratings (top seeds drawn apart)</div>
        </div>
        <button type="button" onClick={() => set('dupr_enabled', !form.dupr_enabled)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full ${form.dupr_enabled ? 'bg-emerald-500' : 'bg-gray-200'}`}
          data-testid="input-dupr-enabled">
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${form.dupr_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>
    )}
  </div>
);

// Step 3: Teams / Players (manual entry for standalone).
// Individual-registration formats collect per-player (no team name); team
// formats keep the team-name + players behaviour.
const Step3Teams = ({ form, teams, setTeams }) => {
  const isIndividual = isIndividualRegistration(form.format);
  const blankPlayer = () => ({ name: '', phone: '', gender: '', dupr_rating: '' });
  const [newTeam, setNewTeam] = useState({ team_name: '', players: [blankPlayer()] });

  const addPlayerInput = () => setNewTeam(t => ({ ...t, players: [...t.players, blankPlayer()] }));
  const removePlayerInput = (idx) => setNewTeam(t => ({ ...t, players: t.players.filter((_, i) => i !== idx) }));
  const setPlayer = (idx, k, v) => setNewTeam(t => ({ ...t, players: t.players.map((p, i) => i === idx ? { ...p, [k]: v } : p) }));

  const canAdd = () => {
    if (!newTeam.players[0].name.trim()) return false;
    if (!isIndividual && !newTeam.team_name.trim()) return false;
    return true;
  };

  const addTeam = () => {
    const cleanPlayers = newTeam.players.filter(p => p.name.trim());
    setTeams(prev => [...prev, {
      // For individual formats the player name IS the identity — use it as the
      // team_name so no spurious "1/2/3" names are created.
      team_name: isIndividual ? cleanPlayers[0].name.trim() : newTeam.team_name.trim(),
      players: cleanPlayers,
    }]);
    setNewTeam({ team_name: '', players: [blankPlayer()] });
  };

  if (form.source === 'linked') {
    return (
      <div className="bg-white border rounded-2xl p-5 space-y-3" data-testid="step-3">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="font-semibold text-sm text-amber-900 mb-1">Linked tournament</div>
          <div className="text-xs text-amber-800">Teams will be imported automatically from the linked Event activity after creation. You'll be able to review the imported team list on the tournament dashboard and remove any team before starting.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border rounded-2xl p-5 space-y-4" data-testid="step-3">
      <div>
        <div className="text-sm font-semibold mb-1">{isIndividual ? 'Add player' : 'Add team (2+ players)'}</div>
        <div className="grid grid-cols-1 gap-2">
          {!isIndividual && (
            <input type="text" placeholder="Team name *" value={newTeam.team_name}
              onChange={e => setNewTeam(t => ({ ...t, team_name: e.target.value }))}
              className="px-3 py-2 border rounded-lg text-sm" data-testid="input-new-team-name" />
          )}
          {newTeam.players.map((p, i) => (
            <div key={i} className="flex gap-2">
              <input type="text" placeholder={isIndividual ? 'Player name *' : `Player ${i + 1} name`} value={p.name}
                onChange={e => setPlayer(i, 'name', e.target.value)}
                className="flex-1 px-3 py-2 border rounded-lg text-sm" data-testid={`input-new-team-player${i}-name`} />
              <input type="text" placeholder="Phone" value={p.phone}
                onChange={e => setPlayer(i, 'phone', e.target.value)}
                className="w-28 px-3 py-2 border rounded-lg text-sm" data-testid={`input-new-team-player${i}-phone`} />
              {form.format === 'mixed_americano' && (
                <select value={p.gender} onChange={e => setPlayer(i, 'gender', e.target.value)}
                  className="px-2 py-2 border rounded-lg text-sm" data-testid={`select-new-team-player${i}-gender`}>
                  <option value="">Gender</option>
                  <option value="male">M</option><option value="female">F</option>
                </select>
              )}
              {form.dupr_enabled && (
                <input type="number" min="0" max="8.5" step="0.1" placeholder="DUPR" value={p.dupr_rating}
                  onChange={e => setPlayer(i, 'dupr_rating', e.target.value)}
                  className="w-20 px-3 py-2 border rounded-lg text-sm" />
              )}
              {!isIndividual && newTeam.players.length > 1 && (
                <button onClick={() => removePlayerInput(i)} className="text-red-500" aria-label="remove">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between items-center mt-2">
          {!isIndividual
            ? <button onClick={addPlayerInput} className="text-xs text-blue-600 hover:underline">+ Add another player</button>
            : <span />}
          <Button size="sm" onClick={addTeam} disabled={!canAdd()} data-testid="add-team-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> {isIndividual ? 'Add player' : 'Add team'}
          </Button>
        </div>
      </div>

      <div className="pt-3 border-t">
        <div className="text-sm font-semibold mb-2">{isIndividual ? 'Players added' : 'Teams added'} <span className="text-xs text-gray-500">({teams.length})</span></div>
        {teams.length === 0 ? (
          <div className="text-xs text-gray-500 py-3 text-center bg-gray-50 rounded-lg">{isIndividual ? `Minimum ${minTeams(form.format)} players required.` : `No teams added yet. Minimum ${minTeams(form.format)} required.`}</div>
        ) : (
          <div className="space-y-1.5">
            {teams.map((t, idx) => (
              <div key={idx} className="flex items-center justify-between bg-gray-50 border rounded-lg px-3 py-2 text-sm" data-testid={`added-team-${idx}`}>
                <div>
                  <div className="font-medium">{isIndividual ? (t.players?.[0]?.name || t.team_name) : t.team_name}</div>
                  <div className="text-xs text-gray-500">{isIndividual ? (t.players?.[0]?.phone || '') : t.players.map(p => p.name).join(', ')}</div>
                </div>
                <button onClick={() => setTeams(prev => prev.filter((_, i) => i !== idx))} className="text-red-500" aria-label={isIndividual ? 'remove player' : 'remove team'} data-testid={`remove-added-team-${idx}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <BracketPreview format={form.format} groupSize={parseInt(form.group_size, 10) || 4} teamCount={teams.length} />
    </div>
  );
};

// FIX 3: BYE banner for KO / DE, plus group distribution preview for Group+KO
const BracketPreview = ({ format, groupSize, teamCount }) => {
  if (teamCount < 2) return null;

  if (format === 'knockout' || format === 'double_elimination') {
    let bracketSize = 1;
    while (bracketSize < teamCount) bracketSize *= 2;
    const byes = bracketSize - teamCount;
    if (byes === 0) return null;
    return (
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 flex gap-2 items-start text-xs text-blue-900" data-testid="bracket-bye-warning">
        <Info className="h-4 w-4 flex-shrink-0 mt-0.5 text-blue-600" />
        <div>
          <span className="font-semibold">{teamCount} teams entered.</span>{' '}
          {byes} BYE match{byes !== 1 ? 'es' : ''} will be added to round out the bracket to {bracketSize} slots. The top {byes} seeded team{byes !== 1 ? 's' : ''} will receive BYEs and auto-advance to Round 2.
        </div>
      </div>
    );
  }

  if (format === 'group_knockout') {
    const numGroups = Math.max(1, Math.ceil(teamCount / Math.max(2, groupSize)));
    // Oversized group size (>= team count) → only one group would form. Match
    // the dashboard's hard-block wording so the review step isn't misleading.
    if (numGroups < 2) {
      return (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex gap-2 items-start text-xs text-amber-900" data-testid="bracket-group-warning">
          <Info className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-600" />
          <div>
            Group size ({groupSize}) must be smaller than the number of teams ({teamCount}) to form at least two groups. Reduce the group size or add more teams before starting.
          </div>
        </div>
      );
    }
    // Snake distribution: sizes balanced as evenly as possible
    const sizes = Array(numGroups).fill(0);
    for (let i = 0; i < teamCount; i++) sizes[i % numGroups] += 1;
    if (sizes.some(s => s < 2)) {
      return (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex gap-2 items-start text-xs text-amber-900" data-testid="bracket-bye-warning">
          <Info className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-600" />
          <div>
            With {teamCount} teams and group size {groupSize}, one group would have only 1 team. Add more teams or reduce the group size.
          </div>
        </div>
      );
    }
    const labels = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
    return (
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900" data-testid="bracket-group-preview">
        <div className="flex gap-2 items-start mb-1.5">
          <Info className="h-4 w-4 flex-shrink-0 mt-0.5 text-blue-600" />
          <div className="font-semibold">{teamCount} teams will be split into {numGroups} group{numGroups !== 1 ? 's' : ''}:</div>
        </div>
        <div className="ml-6 flex flex-wrap gap-x-4 gap-y-0.5">
          {sizes.map((s, i) => (
            <span key={i}>Group {labels[i] || i + 1}: <strong>{s}</strong> team{s !== 1 ? 's' : ''}</span>
          ))}
        </div>
      </div>
    );
  }

  return null;
};

// Step 4: Schedule
const Step4Schedule = ({ form, set }) => (
  <div className="bg-white border rounded-2xl p-5 space-y-4" data-testid="step-4">
    <div className="grid grid-cols-2 gap-3">
      <Field label="Match date"><input type="date" value={form.match_date} onChange={e => set('match_date', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="input-match-date" /></Field>
      <Field label="Start time"><input type="time" value={form.start_time} onChange={e => set('start_time', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="input-start-time" /></Field>
      <Field label="Minutes per match"><input type="number" min="5" max="240" value={form.minutes_per_match} onChange={e => set('minutes_per_match', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="input-minutes-per-match" /></Field>
    </div>
    <div className="text-xs text-gray-500">All schedule fields are optional. You can fill them in later from the tournament dashboard.</div>
  </div>
);

// Step 5: Review
const Step5Review = ({ form, teams }) => (
  <div className="bg-white border rounded-2xl p-5 space-y-3" data-testid="step-5">
    <Row k="Title" v={form.title} />
    <Row k="Sport" v={SPORTS.find(s => s.v === form.sport)?.label} />
    <Row k="Format" v={FORMAT_LABELS[form.format]} />
    <Row k="Courts" v={form.num_courts} />
    <Row k="Source" v={form.source === 'linked' ? 'Linked to event' : 'Standalone'} />
    {form.source !== 'linked' && <Row k="Teams entered" v={teams.length} />}
    {form.match_date && <Row k="Match date" v={form.match_date} />}
    {form.start_time && <Row k="Start time" v={form.start_time} />}
    {form.dupr_enabled && <Row k="DUPR seeding" v="Enabled" />}
    {form.source !== 'linked' && (
      <BracketPreview format={form.format} groupSize={parseInt(form.group_size, 10) || 4} teamCount={teams.length} />
    )}
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 mt-3">
      A unique <strong>referee PIN</strong> will be generated and shown on the tournament dashboard once you click Create.
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-semibold text-gray-700 block mb-1">{label}</label>
    {children}
  </div>
);
const Row = ({ k, v }) => (
  <div className="flex justify-between text-sm border-b last:border-0 py-1.5">
    <span className="text-gray-500">{k}</span>
    <span className="font-medium text-gray-900">{v ?? '—'}</span>
  </div>
);


// ─────────────────────────────────────────────────────────────
// Tournament Dashboard
// ─────────────────────────────────────────────────────────────
const TournamentDashboard = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [tnm, setTnm] = useState(null);
  const [matches, setMatches] = useState([]);
  const [standings, setStandings] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [advancing, setAdvancing] = useState(false);
  const [groupBlock, setGroupBlock] = useState(null);  // empty/invalid group blocks Start
  const [overrideTarget, setOverrideTarget] = useState(null);  // match to override
  const [reportTarget, setReportTarget] = useState(null);  // match to add/view report
  const initialPin = window.history.state?.usr?.initialPin;
  const [pin, setPin] = useState(initialPin || null);
  const [shareRefOpen, setShareRefOpen] = useState(false);
  const [shareStandOpen, setShareStandOpen] = useState(false);
  // FIX 1: skip auto-refresh while admin is editing a score input.
  const focusedInputsRef = React.useRef(0);
  const onInputFocus = useCallback(() => { focusedInputsRef.current += 1; }, []);
  const onInputBlur = useCallback(() => {
    focusedInputsRef.current = Math.max(0, focusedInputsRef.current - 1);
  }, []);

  const load = useCallback(async () => {
    try {
      const data = await api.request(`/tournaments/admin/${id}`);
      setTnm(data);
      setMatches(data.matches || []);
      setStandings(data.standings || []);
      setTeams(data.teams || []);
    } catch (e) {
      toast({ title: 'Failed to load tournament', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // FIX 1: Auto-poll every 15s when tournament is active. Skip if user is
  // editing a score input (focusedInputsRef > 0) or page is hidden.
  const isActive = tnm?.status === 'active';
  useEffect(() => {
    if (!isActive) return undefined;
    const iv = setInterval(() => {
      if (focusedInputsRef.current > 0) return;
      if (document.hidden) return;
      load();
    }, 15000);
    return () => clearInterval(iv);
  }, [isActive, load]);

  const teamById = useMemo(() => Object.fromEntries(teams.map(t => [t.id, t])), [teams]);
  const playerById = useMemo(() => buildPlayerById(teams), [teams]);
  const isIndividual = tnm && INDIVIDUAL_FORMATS.has(tnm.format);

  const start = async () => {
    if (!window.confirm('Start tournament? This will lock the team list and generate Round 1 fixtures.')) return;
    try {
      await api.request(`/tournaments/admin/${id}/start`, { method: 'POST' });
      await load();
      toast({ title: 'Tournament started', description: 'Round 1 fixtures generated' });
    } catch (e) {
      toast({ title: 'Could not start', description: e.message, variant: 'destructive' });
    }
  };

  const advanceRound = async () => {
    setAdvancing(true);
    try {
      const r = await api.request(`/tournaments/admin/${id}/generate-next-round`, { method: 'POST' });
      await load();
      toast({
        title: r.completed ? 'Tournament completed' : 'Round advanced',
        description: r.completed
          ? `Champion: ${teamById[r.champion_team_id]?.team_name || r.champion_team_id || '—'}`
          : `Now on round ${r.current_round}`,
      });
    } catch (e) {
      toast({ title: 'Could not advance round', description: e.message, variant: 'destructive' });
    } finally {
      setAdvancing(false);
    }
  };

  const onScored = async () => { await load(); };
  const removeTeam = async (teamId) => {
    if (!window.confirm('Remove this team?')) return;
    try {
      await api.request(`/tournaments/admin/${id}/teams/${teamId}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      toast({ title: 'Could not remove team', description: e.message, variant: 'destructive' });
    }
  };
  const cancelTournament = async () => {
    if (!window.confirm('Cancel this tournament? Cannot be undone.')) return;
    try {
      await api.request(`/tournaments/admin/${id}/cancel`, { method: 'POST' });
      toast({ title: 'Tournament cancelled' });
      nav('/admin/tournaments');
    } catch (e) {
      toast({ title: 'Could not cancel', description: e.message, variant: 'destructive' });
    }
  };

  if (loading || !tnm) return <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;

  const currentRound = tnm.current_round || 0;
  const currentMatches = matches.filter(m => m.round === currentRound);
  const playableMatches = currentMatches.filter(m =>
    m.status !== 'bye' && m.team1_id && m.team2_id,
  );
  const pendingCount = playableMatches.filter(m => m.status !== 'completed').length;

  // Group-knockout group phase: all group matches scoreable in any order.
  // The "Generate Knockout" button unlocks only when every group match is scored.
  const groupPhaseRounds = tnm.group_phase_rounds || 0;
  const koBuilt = matches.some(m => (m.round || 0) > groupPhaseRounds);
  const inGroupPhase = tnm.format === 'group_knockout' && tnm.status === 'active'
    && groupPhaseRounds > 0 && !koBuilt;
  const groupMatches = matches.filter(m => (m.round || 0) <= groupPhaseRounds && m.group);
  const groupPending = groupMatches.filter(m =>
    m.status !== 'bye' && m.team1_id && m.team2_id && m.status !== 'completed',
  ).length;

  return (
    <div className="p-4 lg:p-6 pb-28 lg:pb-6 max-w-6xl mx-auto" data-testid="tournament-dashboard">
      <button onClick={() => nav('/admin/tournaments')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-3">
        <ArrowLeft className="h-4 w-4" /> All Tournaments
      </button>

      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900">{tnm.title}</h1>
            <StatusPill status={tnm.status} />
            {tnm.status === 'active' && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-medium text-emerald-700" data-testid="live-pill">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Live · auto-refreshes every 15s
              </span>
            )}
          </div>
          <div className="text-sm text-gray-500 flex flex-wrap gap-x-3 gap-y-1">
            <span>{FORMAT_LABELS[tnm.format] || tnm.format}</span>
            <span className="capitalize">{tnm.sport}</span>
            <span>{tnm.num_courts} court{tnm.num_courts !== 1 ? 's' : ''}</span>
            {tnm.match_date && <span>{tnm.match_date}{tnm.start_time ? ` · ${tnm.start_time}` : ''}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          {tnm.status === 'setup' && (
            <div className="flex flex-col items-end gap-1">
              <Button onClick={start}
                disabled={teams.length < minTeams(tnm.format) || (tnm.format === 'group_knockout' && !!groupBlock)}
                data-testid="start-tournament-btn">
                <Trophy className="h-4 w-4 mr-2" /> Start tournament
              </Button>
              {tnm.format === 'group_knockout' && groupBlock && (
                <span className="text-xs text-red-600 max-w-xs text-right" data-testid="start-blocked-message">{groupBlock}</span>
              )}
            </div>
          )}
          {tnm.status === 'active' && (
            inGroupPhase
              ? <AdvanceButton pending={groupPending} onAdvance={advanceRound} loading={advancing}
                  label="Generate Knockout" pendingNoun="group match" />
              : <AdvanceButton pending={pendingCount} onAdvance={advanceRound} loading={advancing} />
          )}
          {['setup', 'active'].includes(tnm.status) && (
            <Button variant="outline" onClick={cancelTournament} data-testid="cancel-tournament-btn">Cancel</Button>
          )}
        </div>
      </div>

      <RefereePinCard tournamentId={id} pin={pin} setPin={setPin} />

      <ShareButtonsRow
        tournament={tnm}
        onShareReferee={() => setShareRefOpen(true)}
        onShareStandings={() => setShareStandOpen(true)}
      />

      <ShareRefereeModal
        open={shareRefOpen}
        onClose={() => setShareRefOpen(false)}
        tournament={tnm}
        pin={pin}
      />
      <ShareStandingsModal
        open={shareStandOpen}
        onClose={() => setShareStandOpen(false)}
        tournament={tnm}
      />


      {tnm.status === 'setup' && (
        <TeamsSetupCard
          tournamentId={id}
          tournament={tnm}
          teams={teams}
          format={tnm.format}
          onRemove={removeTeam}
          source={tnm.source}
          onImported={load}
          onTeamRenamed={load}
        />
      )}

      {/* Racquet scoring config — editable while tournament is in
          setup (before matches are generated). Backend PATCH rejects
          any settings edit once status='active'/'completed', so once
          the tournament starts this becomes a read-only summary. */}
      {tnm && RACQUET_SPORTS.has(tnm.sport) && RACQUET_TEAM_FORMATS.has(tnm.format) && (
        <RacquetScoringConfigCard tournament={tnm} onSaved={load} />
      )}

      {tnm.status === 'active' && tnm.source === 'linked' && (
        <LinkedImportPill tournamentId={id} tournament={tnm} onImported={load} />
      )}

      {tnm.status === 'completed' && tnm.champion_team_id && (
        <Card className="mb-4 border-emerald-300 bg-emerald-50">
          <CardContent className="py-4 flex items-center gap-3">
            <Trophy className="h-8 w-8 text-emerald-600" />
            <div>
              <div className="text-xs uppercase font-semibold text-emerald-700">Champion</div>
              <div className="text-xl font-bold text-emerald-900" data-testid="champion-name">
                {teamById[tnm.champion_team_id]?.team_name || tnm.champion_team_id}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {tnm.status === 'active' && inGroupPhase && (
        <GroupStageCard
          tournament={tnm}
          matches={groupMatches}
          teamById={teamById}
          playerById={playerById}
          onScored={onScored}
          onOverride={setOverrideTarget}
          onReport={setReportTarget}
          onInputFocus={onInputFocus}
          onInputBlur={onInputBlur}
        />
      )}

      {tnm.status === 'active' && !inGroupPhase && currentMatches.length > 0 && (
        <CurrentRoundCard
          tournament={tnm}
          matches={currentMatches}
          teamById={teamById}
          playerById={playerById}
          onScored={onScored}
          onOverride={setOverrideTarget}
          onReport={setReportTarget}
          onInputFocus={onInputFocus}
          onInputBlur={onInputBlur}
        />
      )}

      {/* FIX 4 + FIX 5: Past rounds (read-only listing with Edit Score button on each match). When completed we also include the final round itself. Hidden during group phase — GroupStageCard shows all group matches (scored + unscored). */}
      {!inGroupPhase && (tnm.status === 'active' || tnm.status === 'completed') && (currentRound > 1 || tnm.status === 'completed') && (
        <PastRoundsCard
          tournament={tnm}
          allMatches={matches}
          currentRound={currentRound}
          teamById={teamById}
          playerById={playerById}
          onOverride={setOverrideTarget}
          onReport={setReportTarget}
        />
      )}

      {/* Group setup / pre-play editing (group_knockout only) */}
      {tnm.format === 'group_knockout' && (tnm.status === 'setup' || tnm.status === 'active') && (
        <GroupSetupCard tournament={tnm} teams={teams} onSaved={load} onValidityChange={setGroupBlock} />
      )}

      {/* Qualification / advancement (flexible best-runners-up) — active group_knockout */}
      {tnm.format === 'group_knockout' && tnm.status === 'active' && (
        <QualificationCard tournament={tnm} teams={teams} onChanged={load} />
      )}

      {/* Edit knockout pairings (re-pair who faces whom) — both knockout & group_knockout */}
      {(tnm.format === 'knockout' || tnm.format === 'group_knockout') && tnm.status === 'active' && (
        <EditPairingsCard tournament={tnm} matches={matches} teamById={teamById} onChanged={load} />
      )}

      {/* Team sheet + check-in management (operational; does not affect fixtures) */}
      {(tnm.status === 'setup' || tnm.status === 'active' || tnm.status === 'completed') && (
        <TeamsManagementCard
          tournament={tnm}
          teams={teams}
          allMatches={matches}
          onChanged={load}
        />
      )}

      {/* Individual leaderboard alongside (americano / mexicano / mixed / king) */}
      {isIndividual && standings.length > 0 && (
        <LeaderboardCard format={tnm.format} standings={standings} teamById={teamById} playerById={playerById} />
      )}

      {/* Team standings for round_robin / group_knockout */}
      {!isIndividual && tnm.format !== 'knockout' && tnm.format !== 'double_elimination' && standings.length > 0 && (
        <TeamStandingsCard tournament={tnm} standings={standings} teamById={teamById} />
      )}

      {/* Top scorers (football only) */}
      {tnm.sport === 'football' && (tnm.status === 'active' || tnm.status === 'completed') && (
        <TopScorersCard tournamentId={id} refreshKey={matches} />
      )}

      {/* Score override dialog */}
      {overrideTarget && (
        <ScoreOverrideDialog
          tournamentId={id}
          tournament={tnm}
          match={overrideTarget}
          team1Name={resolveMatchSide({ playerIds: overrideTarget.team1_player_ids, team: teamById[overrideTarget.team1_id], teamId: overrideTarget.team1_id }, playerById, teamById)}
          team2Name={resolveMatchSide({ playerIds: overrideTarget.team2_player_ids, team: teamById[overrideTarget.team2_id], teamId: overrideTarget.team2_id }, playerById, teamById)}
          onClose={() => setOverrideTarget(null)}
          onSaved={() => { setOverrideTarget(null); load(); }}
        />
      )}

      {/* Match report dialog (football) */}
      {reportTarget && (
        <MatchReportDialog
          tournamentId={id}
          match={reportTarget}
          team1={teamById[reportTarget.team1_id]}
          team2={teamById[reportTarget.team2_id]}
          onClose={() => setReportTarget(null)}
          onSaved={() => { setReportTarget(null); load(); }}
        />
      )}
    </div>
  );
};

const QualBracketPreview = ({ teamCount, groupSize, direct, wildcards }) => {
  const numGroups = teamCount >= 2 ? Math.max(1, Math.ceil(teamCount / Math.max(2, groupSize))) : 0;
  if (!numGroups) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-500" data-testid="qual-bracket-preview">
        Add teams (Step 3) to see the number of qualifiers and the resulting knockout bracket size.
      </div>
    );
  }
  const total = direct * numGroups + wildcards;
  let bracket = 2; while (bracket < total) bracket *= 2;
  const byes = Math.max(0, bracket - total);
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900" data-testid="qual-bracket-preview">
      <div><strong>{direct}</strong> per group × {numGroups} groups{wildcards ? <> + <strong>{wildcards}</strong> best runners-up</> : null} = <strong>{total}</strong> qualifiers</div>
      <div className="mt-0.5">Knockout bracket: <strong>{total >= 2 ? bracket : '—'}</strong>{byes ? <> ({byes} bye{byes !== 1 ? 's' : ''})</> : ' (exact, no byes)'}</div>
      {total < 2 && <div className="text-red-600 mt-0.5">Need at least 2 qualifiers.</div>}
    </div>
  );
};

const GroupSetupCard = ({ tournament, teams, onSaved, onValidityChange }) => {
  const locked = !!tournament.groups_locked;
  const activeTeams = useMemo(() => teams.filter(t => t.status === 'active'), [teams]);
  const groupSize = Math.max(2, parseInt(tournament.group_size, 10) || 4);
  const numGroups = Math.max(2, Math.ceil(activeTeams.length / groupSize));
  const labels = useMemo(() => Array.from({ length: numGroups }, (_, i) => String.fromCharCode(65 + i)), [numGroups]);
  const [saving, setSaving] = useState(false);

  // assignment: team_id -> group label. Seed from tnm.groups if present, else snake.
  const [assign, setAssign] = useState({});
  useEffect(() => {
    const seed = {};
    const existing = tournament.groups || null;
    if (existing && Object.keys(existing).length) {
      for (const [g, ids] of Object.entries(existing)) {
        (ids || []).forEach(tid => { seed[tid] = g; });
      }
    }
    // Any team without a placement → snake fill across labels.
    let idx = 0, dir = 1;
    activeTeams.forEach(t => {
      if (!seed[t.id] || !labels.includes(seed[t.id])) {
        seed[t.id] = labels[idx];
        idx += dir;
        if (idx >= labels.length) { dir = -1; idx = labels.length - 1; }
        else if (idx < 0) { dir = 1; idx = 0; }
      }
    });
    setAssign(seed);
  }, [tournament.groups, activeTeams, labels]);

  const groupsMap = useMemo(() => {
    const m = {};
    labels.forEach(l => { m[l] = []; });
    activeTeams.forEach(t => { (m[assign[t.id]] = m[assign[t.id]] || []).push(t.id); });
    return m;
  }, [assign, activeTeams, labels]);

  const sizes = labels.map(l => (groupsMap[l] || []).length);
  const tooLarge = groupSize >= activeTeams.length && activeTeams.length > 0;
  const emptyLabels = labels.filter((l, i) => sizes[i] === 0);
  const singleLabels = labels.filter((l, i) => sizes[i] === 1);
  const undersized = sizes.some(s => s < 2);
  const uneven = Math.max(...sizes) - Math.min(...sizes) > 1;
  const canSave = !locked && !tooLarge && !undersized && activeTeams.length >= 4;

  // Blocking reason for the Start button (empty groups are a hard block,
  // distinct from an uneven-but-valid split which is allowed).
  const blockReason = locked ? null
    : tooLarge ? `Group size (${groupSize}) must be smaller than the number of teams (${activeTeams.length}).`
    : emptyLabels.length ? `Group ${emptyLabels.join(', ')} ${emptyLabels.length > 1 ? 'are' : 'is'} empty. Every group needs at least 2 teams.`
    : singleLabels.length ? `Group ${singleLabels.join(', ')} has only 1 team. Every group needs at least 2 teams.`
    : activeTeams.length < 4 ? 'Add at least 4 teams.'
    : null;
  useEffect(() => { onValidityChange && onValidityChange(blockReason); }, [blockReason, onValidityChange]);

  const save = async () => {
    setSaving(true);
    try {
      await api.request(`/tournaments/admin/${tournament.id}/groups`, {
        method: 'PUT', body: JSON.stringify({ groups: groupsMap }),
      });
      await onSaved();
      toast({ title: 'Groups saved', description: tournament.status === 'active' ? 'Group-stage fixtures regenerated.' : 'Manual group placement saved.' });
    } catch (e) {
      toast({ title: 'Could not save groups', description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Card className="mb-4" data-testid="group-setup-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <UserCog className="h-4 w-4" /> Group Setup
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {locked ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="groups-locked-message">
            Groups are locked once matches have started.
          </div>
        ) : (
          <>
            <p className="text-xs text-gray-500">
              Assign each team to a group. {tournament.status === 'active'
                ? 'Saving regenerates the group-stage fixtures (allowed only before any match is played).'
                : 'Groups will be used when you start the tournament.'}
            </p>
            {tooLarge && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="groups-toolarge-message">
                Group size ({groupSize}) must be smaller than the number of teams ({activeTeams.length}) to form at least two groups.
              </div>
            )}
            <div className="flex flex-wrap gap-2 text-xs" data-testid="group-size-summary">
              {labels.map((l, i) => (
                <span key={l} className={`px-2 py-1 rounded border ${sizes[i] < 2 ? 'border-red-300 bg-red-50 text-red-700' : 'border-gray-200 bg-gray-50 text-gray-700'}`}>
                  Group {l}: <strong>{sizes[i]}</strong>
                </span>
              ))}
            </div>
            {uneven && !undersized && (
              <div className="text-xs text-amber-700">Groups are uneven — that's allowed, just confirming it's intended.</div>
            )}
            <div className="divide-y border rounded-lg">
              {activeTeams.map(t => (
                <div key={t.id} className="flex items-center justify-between gap-2 px-3 py-2" data-testid={`group-row-${t.id}`}>
                  <span className="text-sm font-medium truncate">{t.team_name}</span>
                  <select
                    value={assign[t.id] || labels[0]}
                    onChange={e => setAssign(a => ({ ...a, [t.id]: e.target.value }))}
                    className="px-2 py-1 border rounded text-sm"
                    data-testid={`group-select-${t.id}`}>
                    {labels.map(l => <option key={l} value={l}>Group {l}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <Button size="sm" onClick={save} disabled={!canSave || saving} data-testid="save-groups-btn">
              {saving ? 'Saving…' : (tournament.status === 'active' ? 'Save & Regenerate Fixtures' : 'Save Group Placement')}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const EditPairingsCard = ({ tournament, matches, teamById, onChanged }) => {
  const [slotA, setSlotA] = useState('');
  const [slotB, setSlotB] = useState('');
  const [busy, setBusy] = useState(false);

  const gpr = tournament.group_phase_rounds || 0;
  const koMatches = (matches || []).filter(m => (m.round || 0) > gpr);
  const teamName = (id) => (id ? (teamById[id]?.team_name || id) : null);
  const grpOf = (id) => teamById[id]?.group;
  const isBye = (m) => !!(m.is_bye || m.status === 'bye');
  const isPlayed = (m) => ['ongoing', 'completed'].includes(m.status)
    || m.score_team1 != null || m.score_team2 != null || (m.score_history || []).length > 0;

  // Nothing to do until a bracket exists.
  if (koMatches.length === 0) return null;

  const rounds = [...new Set(koMatches.map(m => m.round))].sort((a, b) => a - b);
  const parse = (v) => { const [mid, pos] = v.split(':'); return { mid, pos: +pos }; };

  // Eligible slots: unplayed, non-bye KO matches, position holds a real team.
  const eligible = koMatches
    .filter(m => !isPlayed(m) && !isBye(m))
    .flatMap(m => [1, 2]
      .filter(p => m[`team${p}_id`])
      .map(p => ({
        value: `${m.id}:${p}`, round: m.round, matchNo: m.match_number,
        teamId: m[`team${p}_id`], label: teamName(m[`team${p}_id`]),
        opp: teamName(m[`team${p === 1 ? 2 : 1}_id`]) || 'TBD',
      })));

  const aRound = slotA ? koMatches.find(m => m.id === parse(slotA).mid)?.round : null;
  const bOptions = eligible.filter(s => s.value !== slotA && (aRound == null || s.round === aRound));

  // Same-group clash preview for the resulting pairings (warn, don't block).
  const clashGroups = (() => {
    if (!slotA || !slotB) return [];
    const A = parse(slotA), B = parse(slotB);
    if (A.mid === B.mid) return []; // flip within one match — no group change
    const mA = koMatches.find(m => m.id === A.mid), mB = koMatches.find(m => m.id === B.mid);
    const teamA = mA[`team${A.pos}_id`], teamB = mB[`team${B.pos}_id`];
    const otherA = mA[`team${A.pos === 1 ? 2 : 1}_id`], otherB = mB[`team${B.pos === 1 ? 2 : 1}_id`];
    const out = [];
    if (grpOf(teamB) && grpOf(teamB) === grpOf(otherA)) out.push(grpOf(teamB));
    if (grpOf(teamA) && grpOf(teamA) === grpOf(otherB)) out.push(grpOf(teamA));
    return [...new Set(out)];
  })();

  const doRepair = async () => {
    if (!slotA || !slotB) return;
    const A = parse(slotA), B = parse(slotB);
    setBusy(true);
    try {
      const res = await api.request(`/tournaments/admin/${tournament.id}/knockout/repair`, {
        method: 'PUT',
        body: JSON.stringify({ match1_id: A.mid, position1: A.pos, match2_id: B.mid, position2: B.pos }),
      });
      await onChanged();
      setSlotA(''); setSlotB('');
      toast({
        title: 'Pairings updated',
        description: res.same_group_clashes
          ? `Note: ${res.same_group_clashes} same-group match(es) in this round.` : 'Who-faces-whom updated.',
      });
    } catch (e) {
      toast({ title: 'Could not re-pair', description: e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const slotOptLabel = (s) => `R${s.round} M${s.matchNo}: ${s.label} (vs ${s.opp})`;
  const hasEditable = eligible.length >= 2;

  return (
    <Card className="mb-4" data-testid="edit-pairings-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Shuffle className="h-4 w-4" /> Edit Pairings
        </CardTitle>
        <p className="text-xs text-gray-500">Rearrange who faces whom (same teams). This is different from
          replacing a team — it only swaps two teams' positions.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Bracket view — editable vs frozen */}
        {rounds.map(r => (
          <div key={r}>
            <div className="text-xs font-semibold uppercase text-gray-500 mb-1" data-testid={`ko-round-heading-${r}`}>
              {getRoundName(tournament, r, koMatches.find(m => m.round === r))}
            </div>
            <div className="space-y-1">
              {koMatches.filter(m => m.round === r).sort((a, b) => (a.match_number || 0) - (b.match_number || 0)).map(m => {
                const bye = isBye(m), played = isPlayed(m);
                const frozen = bye || played;
                const t2label = bye ? 'BYE' : (teamName(m.team2_id) || 'TBD');
                return (
                  <div key={m.id}
                    className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded border text-sm ${frozen ? 'bg-gray-50 border-gray-200' : 'bg-white border-blue-200'}`}
                    data-testid={`ko-pair-row-${m.id}`}>
                    <span className="truncate">
                      <span className="text-gray-400 mr-1">M{m.match_number}</span>
                      {teamName(m.team1_id) || 'TBD'} <span className="text-gray-400">v</span> {t2label}
                    </span>
                    {frozen && (
                      <span className="flex items-center gap-1 text-[11px] text-gray-500 shrink-0" data-testid={`ko-pair-frozen-${m.id}`}>
                        <Lock className="h-3 w-3" /> {played ? 'Played' : 'Bye'}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Bye explanation (only when a bye exists) */}
        {koMatches.some(m => isBye(m)) && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-2.5 flex gap-2 items-start text-[11px] text-gray-600" data-testid="ko-bye-note">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span><strong>Byes can't be re-paired.</strong> To change who receives a bye, re-seed the bracket before any
              knockout match is played. Once a knockout match has been played, byes can no longer be changed.</span>
          </div>
        )}

        {/* Swap controls */}
        {hasEditable ? (
          <div className="rounded-lg border p-3 space-y-2" data-testid="ko-repair-panel">
            <p className="text-xs text-gray-600">Swap two teams' positions (unplayed matches, same round).</p>
            <div className="flex flex-wrap gap-2 items-center">
              <select className="px-2 py-1 border rounded text-sm max-w-[45%]" data-testid="repair-slot-a"
                value={slotA} onChange={e => { setSlotA(e.target.value); setSlotB(''); }}>
                <option value="">Move this team…</option>
                {eligible.map(s => <option key={s.value} value={s.value}>{slotOptLabel(s)}</option>)}
              </select>
              <Shuffle className="h-4 w-4 text-gray-400" />
              <select className="px-2 py-1 border rounded text-sm max-w-[45%]" data-testid="repair-slot-b"
                value={slotB} disabled={!slotA} onChange={e => setSlotB(e.target.value)}>
                <option value="">…with this team</option>
                {bOptions.map(s => <option key={s.value} value={s.value}>{slotOptLabel(s)}</option>)}
              </select>
              <Button size="sm" onClick={doRepair} disabled={busy || !slotA || !slotB} data-testid="repair-btn">
                {busy && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}Swap positions
              </Button>
            </div>
            {clashGroups.length > 0 && (
              <div className="rounded border border-amber-300 bg-amber-50 p-2 flex gap-2 items-start text-[11px] text-amber-800" data-testid="ko-samegroup-warning">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>This pairs two teams from the same group ({clashGroups.join(', ')}). Allowed — just confirming it's intentional.</span>
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-gray-400" data-testid="ko-repair-none">No unplayed matches available to re-pair.</p>
        )}
      </CardContent>
    </Card>
  );
};

const QualificationCard = ({ tournament, teams, onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [swapOld, setSwapOld] = useState('');
  const [swapNew, setSwapNew] = useState('');
  const [wcPicks, setWcPicks] = useState([]);
  const teamName = (id) => teams.find(t => t.id === id)?.team_name || id;
  const labels = forAgainstLabels(tournament?.sport);
  const load = useCallback(async () => {
    try { setData(await api.request(`/tournaments/admin/${tournament.id}/qualifiers`)); }
    catch (e) { /* group stage may not be complete yet */ setData({ error: e.message }); }
  }, [tournament.id]);
  useEffect(() => { load(); }, [load]);

  if (!data || data.error) return null;
  const quals = data.qualifiers || [];
  const activeIds = teams.filter(t => t.status === 'active').map(t => t.id);
  const qualIds = new Set(quals.map(q => q.team_id));
  const nonQualified = activeIds.filter(id => !qualIds.has(id));
  const badge = (as) => as === 'winner' ? 'bg-emerald-100 text-emerald-800'
    : as === 'wildcard' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800';

  const overrideSeed = async (seedIdx, newTeamId) => {
    const next = quals.map((q, i) => i === seedIdx ? { ...q, team_id: newTeamId } : q);
    setBusy(true);
    try {
      await api.request(`/tournaments/admin/${tournament.id}/qualifiers`, {
        method: 'PUT', body: JSON.stringify({ qualifiers: next }) });
      await load(); await onChanged();
      toast({ title: 'Qualifiers updated', description: data.ko_built ? 'Knockout bracket regenerated.' : 'Saved.' });
    } catch (e) { toast({ title: 'Could not update qualifiers', description: e.message, variant: 'destructive' }); }
    finally { setBusy(false); }
  };
  const doSwap = async () => {
    if (!swapOld || !swapNew) return;
    setBusy(true);
    try {
      await api.request(`/tournaments/admin/${tournament.id}/qualifiers/swap`, {
        method: 'PUT', body: JSON.stringify({ old_team_id: swapOld, new_team_id: swapNew }) });
      await load(); await onChanged();
      setSwapOld(''); setSwapNew('');
      toast({ title: 'Qualifier swapped' });
    } catch (e) { toast({ title: 'Swap not allowed', description: e.message, variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const revertToAuto = async () => {
    if (!window.confirm('Revert to auto-computed qualifiers? Any manual seed changes will be replaced by the current standings order.')) return;
    setBusy(true);
    try {
      const r = await api.request(`/tournaments/admin/${tournament.id}/qualifiers`, { method: 'DELETE' });
      await load(); await onChanged();
      toast({ title: 'Reverted to auto',
              description: r.regenerated ? 'Knockout bracket regenerated.'
                          : r.needs_manual ? 'Pick wildcards manually — groups are unequal.'
                          : 'Qualifiers now match current standings.' });
    } catch (e) {
      toast({ title: 'Could not revert', description: e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const wildcardsNeeded = Math.max(0, (data.total || 0) - (data.auto_qualifiers || []).length);
  const saveManualWildcards = async () => {
    const base = (data.auto_qualifiers || []).map((q, i) => ({ ...q, seed: i + 1 }));
    const picks = wcPicks.map((tid, i) => ({
      team_id: tid, qualified_as: 'wildcard', seed: base.length + i + 1,
      group: (data.candidates.find(c => c.team_id === tid) || {}).group,
    }));
    setBusy(true);
    try {
      await api.request(`/tournaments/admin/${tournament.id}/qualifiers`, {
        method: 'PUT', body: JSON.stringify({ qualifiers: [...base, ...picks] }) });
      setWcPicks([]);
      await load(); await onChanged();
      toast({ title: 'Wildcards selected', description: 'Qualifiers saved — you can now generate the knockout bracket.' });
    } catch (e) { toast({ title: 'Could not save wildcards', description: e.message, variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  return (
    <Card className="mb-4" data-testid="qualification-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><Trophy className="h-4 w-4" /> Qualification / Advancement</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2 text-xs" data-testid="qual-summary">
          <span className="px-2 py-1 rounded border bg-gray-50">{data.total} qualifiers → bracket of {data.bracket_size}{data.byes ? ` (${data.byes} byes)` : ''}</span>
          {data.overridden && <span className="px-2 py-1 rounded bg-purple-100 text-purple-800">Manually overridden</span>}
          {data.ko_locked && <span className="px-2 py-1 rounded bg-amber-100 text-amber-800">Knockout started — swap only</span>}
          {data.overridden && !data.ko_locked && (
            <button type="button" onClick={revertToAuto} disabled={busy}
                    className="px-2 py-1 rounded border border-purple-300 text-purple-800 bg-white hover:bg-purple-50 disabled:opacity-50"
                    data-testid="qual-revert-to-auto-btn">
              Revert to auto
            </button>
          )}
        </div>
        {data.needs_manual && !data.overridden && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 space-y-2" data-testid="qual-needs-manual">
            <div>
              Groups are unequal — a team in a bigger group has played more games, so automatic best runners-up are disabled.
              Select <strong>{wildcardsNeeded}</strong> wildcard{wildcardsNeeded !== 1 ? 's' : ''} from the ranked runners-up below.
            </div>
            <div className="border border-amber-200 rounded bg-white divide-y" data-testid="wildcard-picker">
              {(data.candidates || []).map((c) => {
                const picked = wcPicks.includes(c.team_id);
                const full = wcPicks.length >= wildcardsNeeded;
                return (
                  <div key={c.team_id} className="flex items-center justify-between gap-2 px-2 py-1.5" data-testid={`wildcard-cand-${c.team_id}`}>
                    <span className="truncate text-gray-800">
                      {c.team_name} <span className="text-gray-400">· {c.group} · {c.points}pts {labels.diff} {c.goal_difference} {labels.for} {c.goals_for} {labels.against} {c.goals_against}</span>
                    </span>
                    <button type="button" disabled={busy || (!picked && full)}
                      onClick={() => setWcPicks(p => picked ? p.filter(x => x !== c.team_id) : [...p, c.team_id])}
                      className={`px-2 py-0.5 rounded text-[11px] font-medium border ${picked ? 'bg-amber-600 text-white border-amber-600' : full ? 'border-gray-200 text-gray-300' : 'border-amber-400 text-amber-700'}`}
                      data-testid={`wildcard-toggle-${c.team_id}`}>
                      {picked ? `Wildcard #${wcPicks.indexOf(c.team_id) + 1}` : 'Add'}
                    </button>
                  </div>
                );
              })}
            </div>
            <Button size="sm" onClick={saveManualWildcards}
              disabled={busy || wcPicks.length !== wildcardsNeeded}
              data-testid="save-wildcards-btn">
              {`Confirm ${wcPicks.length}/${wildcardsNeeded} wildcards`}
            </Button>
          </div>
        )}
        {/* Qualifiers list */}
        <div className="divide-y border rounded-lg">
          {quals.map((q, i) => (
            <div key={q.team_id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm" data-testid={`qualifier-row-${i}`}>
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-gray-400 w-5">#{q.seed || i + 1}</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${badge(q.qualified_as)}`}>{q.qualified_as}{q.group ? ` · ${q.group}` : ''}</span>
                <span className="font-medium truncate">{teamName(q.team_id)}</span>
              </div>
              {!data.ko_locked && nonQualified.length > 0 && (
                <select className="px-2 py-1 border rounded text-xs" data-testid={`qualifier-swap-${i}`}
                  value="" disabled={busy}
                  onChange={e => e.target.value && overrideSeed(i, e.target.value)}>
                  <option value="">Replace…</option>
                  {nonQualified.map(id => <option key={id} value={id}>{teamName(id)}</option>)}
                </select>
              )}
            </div>
          ))}
        </div>
        {/* Post-play swap controls */}
        {data.ko_locked && (
          <div className="rounded-lg border p-3 space-y-2" data-testid="qual-swap-panel">
            <p className="text-xs text-gray-600">Knockout has started. You can only replace a team whose matches haven't been played yet.</p>
            <div className="flex flex-wrap gap-2 items-center">
              <select className="px-2 py-1 border rounded text-sm" data-testid="swap-old" value={swapOld} onChange={e => setSwapOld(e.target.value)}>
                <option value="">Team to remove…</option>
                {quals.map(q => <option key={q.team_id} value={q.team_id}>{teamName(q.team_id)}</option>)}
              </select>
              <span className="text-gray-400">→</span>
              <select className="px-2 py-1 border rounded text-sm" data-testid="swap-new" value={swapNew} onChange={e => setSwapNew(e.target.value)}>
                <option value="">Replacement…</option>
                {nonQualified.map(id => <option key={id} value={id}>{teamName(id)}</option>)}
              </select>
              <Button size="sm" onClick={doSwap} disabled={busy || !swapOld || !swapNew} data-testid="swap-btn">Swap</Button>
            </div>
          </div>
        )}
        {/* Best runners-up ranking table (justification) */}
        {data.candidates && data.candidates.length > 0 && (
          <details className="text-xs" data-testid="runners-up-ranking">
            <summary className="cursor-pointer text-gray-600">Best runners-up ranking (FIFA order)</summary>
            <table className="w-full mt-2 text-left">
              <thead className="text-gray-500"><tr><th className="py-1">#</th><th>Team</th><th>Grp</th><th>Pts</th><th>{labels.diff}</th><th>{labels.for}</th><th>{labels.against}</th></tr></thead>
              <tbody>
                {data.candidates.map((c, i) => (
                  <tr key={c.team_id} className="border-t"><td className="py-1">{i + 1}</td><td>{c.team_name || teamName(c.team_id)}</td><td>{c.group}</td><td>{c.points}</td><td>{c.goal_difference}</td><td>{c.goals_for}</td><td>{c.goals_against}</td></tr>
                ))}
              </tbody>
            </table>
          </details>
        )}
      </CardContent>
    </Card>
  );
};

const AdvanceButton = ({ pending, onAdvance, loading, label = 'Generate Next Round', pendingNoun = 'match' }) => (
  <div className="flex flex-col items-end gap-1">
    <Button onClick={onAdvance} disabled={pending > 0 || loading} data-testid="advance-round-btn">
      {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
      {label}
    </Button>
    {pending > 0 && (
      <div className="text-[11px] text-amber-700 flex items-center gap-1" data-testid="advance-pending-msg">
        <AlertCircle className="h-3 w-3" /> {pending} {pendingNoun}{pending !== 1 ? 'es' : ''} still pending
      </div>
    )}
  </div>
);

// Referee PIN card with copy button
const RefereePinCard = ({ tournamentId, pin, setPin }) => {
  const [rotating, setRotating] = useState(false);

  const rotate = async () => {
    if (!window.confirm('Generate a NEW referee PIN? The old PIN will stop working.')) return;
    setRotating(true);
    try {
      const r = await api.request(`/tournaments/admin/${tournamentId}/regenerate-pin`, { method: 'POST' });
      setPin(r.referee_pin);
      toast({ title: 'New PIN generated' });
    } catch (e) {
      toast({ title: 'Could not regenerate PIN', description: e.message, variant: 'destructive' });
    } finally { setRotating(false); }
  };

  const copyPin = async () => {
    if (!pin) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(pin);
      } else {
        // Fallback for restricted clipboard contexts
        const el = document.createElement('textarea');
        el.value = pin;
        el.style.position = 'fixed';
        el.style.left = '-9999px';
        document.body.appendChild(el);
        el.focus(); el.select();
        try { document.execCommand('copy'); } finally { document.body.removeChild(el); }
      }
      toast({ title: 'PIN copied' });
    } catch (_) {
      toast({ title: `PIN: ${pin}`, description: 'Copy not allowed by browser — read above' });
    }
  };

  return (
    <Card className="mb-4 border-blue-200 bg-blue-50/50" data-testid="referee-pin-card">
      <CardContent className="py-3 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase font-semibold text-blue-700 tracking-wide">Referee PIN</div>
          <div className="flex items-center gap-3">
            <div className="text-3xl font-bold text-blue-900 font-mono tracking-widest" data-testid="referee-pin-value">
              {pin || '••••'}
            </div>
            {pin && (
              <button onClick={copyPin} className="text-blue-600 hover:text-blue-800 p-1.5 rounded-md hover:bg-blue-100" data-testid="copy-pin-btn">
                <Copy className="h-4 w-4" />
              </button>
            )}
          </div>
          {!pin && <div className="text-[11px] text-blue-700 mt-0.5">Click "New PIN" to view or rotate</div>}
        </div>
        <Button variant="outline" size="sm" onClick={rotate} disabled={rotating} data-testid="rotate-pin-btn">
          {rotating ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
          New PIN
        </Button>
      </CardContent>
    </Card>
  );
};


// ─────────────────────────────────────────────────────────────
// Share buttons + modals
// ─────────────────────────────────────────────────────────────

const PUBLIC_BASE = 'https://playspaces.in';
const REFEREE_URL = `${PUBLIC_BASE}/referee`;
const publicTournamentUrl = (slug) => `${PUBLIC_BASE}/tournaments/${slug}`;
const fixturesPdfUrl = (slug) => `${process.env.REACT_APP_BACKEND_URL}/api/tournaments/public/${slug}/fixtures.pdf?t=${Date.now()}`;

// Don't show share buttons for cancelled tournaments
const ShareButtonsRow = ({ tournament, onShareReferee, onShareStandings }) => {
  if (!tournament || tournament.status === 'cancelled') return null;
  return (
    <div className="flex flex-col sm:flex-row gap-2 mb-4" data-testid="share-buttons-row">
      <Button
        variant="outline"
        onClick={onShareReferee}
        className="flex-1 sm:flex-initial border-blue-200 text-blue-700 hover:bg-blue-50"
        data-testid="share-referee-btn"
      >
        <UserCog className="h-4 w-4 mr-2" /> Share Referee Link
      </Button>
      <Button
        variant="outline"
        onClick={onShareStandings}
        className="flex-1 sm:flex-initial border-emerald-200 text-emerald-700 hover:bg-emerald-50"
        data-testid="share-standings-btn"
      >
        <BarChart3 className="h-4 w-4 mr-2" /> Share Live Standings
      </Button>
      {tournament.status !== 'setup' && (
        <a
          href={fixturesPdfUrl(tournament.slug)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center flex-1 sm:flex-initial rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 h-10 px-4 py-2 text-sm font-medium"
          data-testid="download-fixtures-pdf-btn"
        >
          <Download className="h-4 w-4 mr-2" /> Download Fixtures PDF
        </a>
      )}
    </div>
  );
};

const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const el = document.createElement('textarea');
    el.value = text;
    el.style.position = 'fixed';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.focus(); el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  } catch (_) {
    return false;
  }
};

const ShareRefereeModal = ({ open, onClose, tournament, pin }) => {
  if (!tournament) return null;
  const isActive = tournament.status === 'active';
  const title = tournament.title || 'Tournament';
  const message = pin
    ? `Join as referee for ${title}:\nURL: ${REFEREE_URL}\nPIN: ${pin}`
    : '';

  const copyMessage = async () => {
    if (!message) return;
    const ok = await copyToClipboard(message);
    toast({ title: ok ? 'Message copied' : 'Copy not allowed by browser' });
  };
  const copyLink = async () => {
    const ok = await copyToClipboard(REFEREE_URL);
    toast({ title: ok ? 'Link copied' : 'Copy not allowed by browser' });
  };
  const waHref = message
    ? `https://wa.me/?text=${encodeURIComponent(message)}`
    : '#';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="share-referee-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="h-5 w-5 text-blue-600" /> Referee Portal
          </DialogTitle>
          <DialogDescription>
            Send this link and PIN to the people refereeing matches.
          </DialogDescription>
        </DialogHeader>

        {!isActive && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="share-referee-setup-msg">
            Start the tournament first to generate the referee PIN.
          </div>
        )}

        {isActive && (
          <>
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-center">
              <div className="text-[10px] uppercase font-semibold text-blue-700 tracking-wide mb-1">Referee PIN</div>
              <div className="text-4xl font-bold text-blue-900 font-mono tracking-widest" data-testid="share-referee-pin">
                {pin || '••••'}
              </div>
              {!pin && (
                <div className="text-[11px] text-blue-700 mt-2">
                  PIN hidden — click "New PIN" on the dashboard to view & rotate.
                </div>
              )}
            </div>

            <div>
              <div className="text-[11px] uppercase font-semibold text-gray-500 tracking-wide mb-1">Referee URL</div>
              <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 break-all font-mono" data-testid="share-referee-url">
                {REFEREE_URL}
              </div>
            </div>

            {pin && (
              <div>
                <div className="text-[11px] uppercase font-semibold text-gray-500 tracking-wide mb-1">Message to share</div>
                <pre className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 whitespace-pre-wrap font-sans" data-testid="share-referee-message">
{message}
                </pre>
              </div>
            )}
          </>
        )}

        <DialogFooter className="flex flex-col gap-2 sm:flex-col">
          {isActive && pin && (
            <>
              <Button onClick={copyMessage} className="w-full" data-testid="share-referee-copy-message">
                <Copy className="h-4 w-4 mr-2" /> Copy Message
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={copyLink} className="flex-1" data-testid="share-referee-copy-link">
                  <LinkIcon className="h-4 w-4 mr-2" /> Copy Link Only
                </Button>
                <a
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-[#25D366] text-white text-sm font-medium hover:bg-[#20bd5a] transition"
                  data-testid="share-referee-whatsapp"
                >
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
              </div>
            </>
          )}
          {(!isActive || !pin) && (
            <Button variant="outline" onClick={copyLink} className="w-full" data-testid="share-referee-copy-link-only">
              <LinkIcon className="h-4 w-4 mr-2" /> Copy Referee URL
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ShareStandingsModal = ({ open, onClose, tournament }) => {
  if (!tournament) return null;
  const title = tournament.title || 'Tournament';
  const url = publicTournamentUrl(tournament.slug);
  const message = `Follow ${title} live — see brackets, scores and standings:\n${url}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`;

  const copyMessage = async () => {
    const ok = await copyToClipboard(message);
    toast({ title: ok ? 'Message copied' : 'Copy not allowed by browser' });
  };
  const copyLink = async () => {
    const ok = await copyToClipboard(url);
    toast({ title: ok ? 'Link copied' : 'Copy not allowed by browser' });
  };
  const waHref = `https://wa.me/?text=${encodeURIComponent(message)}`;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="share-standings-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-emerald-600" /> Live Standings &amp; Bracket
          </DialogTitle>
          <DialogDescription>
            Share the public spectator page. Auto-refreshes every 30 seconds.
          </DialogDescription>
        </DialogHeader>

        <div>
          <div className="text-[11px] uppercase font-semibold text-gray-500 tracking-wide mb-1">Public URL</div>
          <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 break-all font-mono" data-testid="share-standings-url">
            {url}
          </div>
        </div>

        <div>
          <div className="text-[11px] uppercase font-semibold text-gray-500 tracking-wide mb-1">Message to share</div>
          <pre className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 whitespace-pre-wrap font-sans" data-testid="share-standings-message">
{message}
          </pre>
        </div>

        <div className="flex flex-col items-center gap-2">
          <div className="text-[11px] uppercase font-semibold text-gray-500 tracking-wide">Scan at the venue</div>
          <div className="rounded-lg border border-gray-200 bg-white p-2">
            <img
              src={qrSrc}
              alt="QR code for live standings"
              width={200}
              height={200}
              className="block"
              data-testid="share-standings-qr"
            />
          </div>
        </div>

        <DialogFooter className="flex flex-col gap-2 sm:flex-col">
          <Button onClick={copyMessage} className="w-full" data-testid="share-standings-copy-message">
            <Copy className="h-4 w-4 mr-2" /> Copy Message
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={copyLink} className="flex-1" data-testid="share-standings-copy-link">
              <LinkIcon className="h-4 w-4 mr-2" /> Copy Link Only
            </Button>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-[#25D366] text-white text-sm font-medium hover:bg-[#20bd5a] transition"
              data-testid="share-standings-whatsapp"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// Pre-start: team list + import button for linked
// Inline pencil-to-edit team name. Backend PATCH /admin/{tid}/teams/
// {team_id} accepts `{team_name}` with no status guard, so this works
// before AND after tournament start — matches keep referencing team_id
// under the hood. Hidden when `disabled` (e.g. tournament completed).
const EditableTeamName = ({ tournamentId, team, onSaved, disabled = false,
                           className = 'font-medium text-sm' }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(team.team_name || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(team.team_name || ''); }, [team.team_name]);

  const save = async () => {
    const trimmed = (value || '').trim();
    if (!trimmed) {
      toast({ title: 'Team name cannot be empty', variant: 'destructive' });
      return;
    }
    if (trimmed === team.team_name) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.request(`/tournaments/admin/${tournamentId}/teams/${team.id}`, {
        method: 'PATCH', body: JSON.stringify({ team_name: trimmed }),
      });
      toast({ title: 'Team name updated' });
      setEditing(false);
      await onSaved?.();
    } catch (e) {
      toast({ title: 'Could not update', description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const cancel = () => { setValue(team.team_name || ''); setEditing(false); };

  if (editing) {
    return (
      <div className="flex items-center gap-1" data-testid={`edit-team-name-row-${team.id}`}>
        <Input
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }}
          disabled={saving}
          autoFocus
          className="h-7 text-sm min-w-0 w-40"
          data-testid={`edit-team-name-input-${team.id}`}
        />
        <button type="button" onClick={save} disabled={saving}
          className="text-emerald-600 hover:text-emerald-700 p-0.5"
          aria-label="save team name"
          data-testid={`edit-team-name-save-${team.id}`}>
          <CheckCircle2 className="h-4 w-4" />
        </button>
        <button type="button" onClick={cancel} disabled={saving}
          className="text-gray-500 hover:text-gray-700 p-0.5"
          aria-label="cancel edit"
          data-testid={`edit-team-name-cancel-${team.id}`}>
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <span className={className} data-testid={`team-name-${team.id}`}>{team.team_name}</span>
      {!disabled && (
        <button type="button" onClick={() => setEditing(true)}
          className="text-gray-400 hover:text-blue-600 p-0.5 transition"
          aria-label="rename team"
          data-testid={`rename-team-btn-${team.id}`}>
          <Pencil className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};


const TeamsSetupCard = ({ tournamentId, tournament, teams, format, onRemove, source, onImported, onTeamRenamed }) => {
  const min = minTeams(format);
  const [importing, setImporting] = useState(false);
  const [lastImport, setLastImport] = useState(null);

  const runImport = async () => {
    setImporting(true);
    try {
      const r = await api.request(`/tournaments/admin/${tournamentId}/import-from-event`, { method: 'POST' });
      setLastImport(r);
      const msg = r.imported > 0
        ? `${r.imported} team${r.imported !== 1 ? 's' : ''} imported`
        : 'No new teams to import';
      toast({ title: 'Import complete', description: msg });
      await onImported();
    } catch (e) {
      toast({ title: 'Import failed', description: e.message, variant: 'destructive' });
    } finally { setImporting(false); }
  };

  return (
    <Card className="mb-4">
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center justify-between">
        <span>Teams ({teams.length})</span>
        {teams.length < min && <span className="text-xs text-amber-600 font-normal">Need at least {min}</span>}
      </CardTitle></CardHeader>
      <CardContent>
        {source === 'linked' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 flex items-center justify-between gap-2 flex-wrap" data-testid="linked-import-banner">
            <div className="text-xs text-blue-900">
              <div className="font-semibold">
                {teams.length} {teams.length === 1 ? 'team' : 'teams'} imported
                {tournament?.linked_event_title && (
                  <span> from <span className="font-bold">{tournament.linked_event_title}</span></span>
                )}
                {tournament?.linked_activity_title && (
                  <span> — {tournament.linked_activity_title}</span>
                )}
              </div>
              {tournament?.last_import_at && (
                <div className="text-[11px] text-blue-700 mt-0.5">
                  Last refreshed: {new Date(tournament.last_import_at).toLocaleString()}
                </div>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={runImport} disabled={importing}
              data-testid="refresh-import-btn">
              {importing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
              Refresh Import
            </Button>
          </div>
        )}
        {teams.length === 0 ? (
          <div className="text-xs text-gray-500 py-3 text-center">
            {source === 'linked' ? 'No paid registrations found yet. Use "Refresh Import" after registrations come in.' : 'No teams yet.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {teams.map(t => (
              <div key={t.id} className="flex items-center justify-between bg-gray-50 border rounded-lg px-3 py-2 text-sm" data-testid={`team-row-${t.id}`}>
                <div>
                  <EditableTeamName tournamentId={tournamentId} team={t} onSaved={onTeamRenamed} />
                  <div className="text-xs text-gray-500">{(t.players || []).map(p => p.name).join(', ') || '—'}</div>
                  {t.source === 'imported' && <span className="text-[10px] text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded uppercase mt-0.5 inline-block">Imported</span>}
                </div>
                <button onClick={() => onRemove(t.id)} className="text-red-500" aria-label="remove team" data-testid={`remove-team-${t.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Racquet scoring config card — shown on the dashboard for
// padel/pickleball/tennis team-format tournaments. Editable while
// status='setup'; read-only summary once status flips to active/
// completed (backend PATCH already rejects settings edits post-
// start, so read-only mode is enforced on both ends).
const RacquetScoringConfigCard = ({ tournament, onSaved }) => {
  const isSetup = tournament.status === 'setup';
  const seed = tournament.scoring_by_stage && Object.keys(tournament.scoring_by_stage).length
    ? tournament.scoring_by_stage
    : defaultScoringByStage(tournament.format);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(seed);
  const [saving, setSaving] = useState(false);

  // Re-sync when tournament reloads.
  useEffect(() => {
    setValue(tournament.scoring_by_stage && Object.keys(tournament.scoring_by_stage).length
      ? tournament.scoring_by_stage
      : defaultScoringByStage(tournament.format));
  }, [tournament.scoring_by_stage, tournament.format]);

  const save = async () => {
    const errs = validateScoringByStage(value);
    if (errs.length) {
      toast({ title: 'Fix scoring per stage', description: errs[0], variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const cleaned = {};
      for (const [k, v] of Object.entries(value || {})) {
        if (v == null) continue;
        cleaned[k] = normalizeStageCfg(v);
      }
      await api.request(`/tournaments/admin/${tournament.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ scoring_by_stage: cleaned }),
      });
      toast({ title: 'Scoring config saved' });
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast({ title: 'Could not save', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setValue(seed);
    setEditing(false);
  };

  const stages = stageKeysForFormat(tournament.format);

  return (
    <Card className="mb-4" data-testid="racquet-scoring-card">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle className="text-base">Scoring per stage</CardTitle>
          <div className="text-xs text-gray-500">
            {isSetup
              ? 'Set best-of, games target and tiebreak rules per stage. Edits are locked once the tournament starts.'
              : 'Tournament has started — scoring config is now read-only.'}
          </div>
        </div>
        {isSetup && !editing && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}
            data-testid="racquet-scoring-edit-btn">
            <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
          </Button>
        )}
      </CardHeader>
      <CardContent className="pt-2">
        {editing ? (
          <>
            <RacquetScoringEditor
              value={value}
              format={tournament.format}
              onChange={setValue}
            />
            <div className="flex gap-2 mt-3">
              <Button variant="outline" onClick={cancel} disabled={saving}
                data-testid="racquet-scoring-cancel-btn">Cancel</Button>
              <Button onClick={save} disabled={saving}
                data-testid="racquet-scoring-save-btn">
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </>
        ) : (
          <div className="space-y-1.5" data-testid="racquet-scoring-summary">
            {stages.map(k => {
              const cfg = value?.[k];
              const following = k !== 'default' && cfg == null;
              const eff = following ? value?.default : cfg;
              if (!eff) return null;
              return (
                <div key={k} className="flex flex-wrap items-baseline gap-2 text-sm"
                     data-testid={`racquet-scoring-summary-${k}`}>
                  <span className="font-semibold text-gray-800 w-40 shrink-0">{STAGE_LABELS[k] || k}</span>
                  {following ? (
                    <span className="text-gray-500 italic">Same as default</span>
                  ) : (
                    <span className="text-gray-700">
                      Best of {eff.best_of_sets} · race to {eff.games_target}
                      {eff.tiebreak_at != null
                        ? ` · tiebreak at ${eff.tiebreak_at}-${eff.tiebreak_at} (first to ${eff.tiebreak_target}, win by ${eff.tiebreak_win_by})`
                        : ' · no tiebreak'}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};



// Compact "Refresh Import" pill for ACTIVE tournaments. Shows a warning
// when new teams are added mid-tournament since they cannot be slotted
// into existing fixtures — they will only participate in newly-generated
// rounds (e.g. the next Americano rotation, or future bracket layers if
// admin restarts the tournament).
const LinkedImportPill = ({ tournamentId, tournament, onImported }) => {
  const [importing, setImporting] = useState(false);
  const [warning, setWarning] = useState(null);

  const runImport = async () => {
    setImporting(true);
    try {
      const r = await api.request(`/tournaments/admin/${tournamentId}/import-from-event`, { method: 'POST' });
      if (r.imported > 0 && r.tournament_was_active) {
        setWarning(`${r.imported} new team${r.imported !== 1 ? 's' : ''} added. They will enter from the next round (existing fixtures unchanged).`);
      } else if (r.imported > 0) {
        toast({ title: 'Import complete', description: `${r.imported} team(s) imported` });
      } else {
        toast({ title: 'No new teams', description: 'Everyone is already imported.' });
      }
      await onImported();
    } catch (e) {
      toast({ title: 'Import failed', description: e.message, variant: 'destructive' });
    } finally { setImporting(false); }
  };

  return (
    <Card className="mb-4 border-blue-200 bg-blue-50/40" data-testid="linked-import-pill">
      <CardContent className="py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-blue-900">
          <span className="font-semibold">Linked to {tournament?.linked_event_title || 'event'}</span>
          {tournament?.linked_activity_title && (
            <span> — {tournament.linked_activity_title}</span>
          )}
          {warning && (
            <div className="text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1 inline-flex items-center gap-1" data-testid="late-import-warning">
              <AlertCircle className="h-3 w-3" /> {warning}
            </div>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={runImport} disabled={importing} data-testid="refresh-import-btn-active">
          {importing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
          Refresh Import
        </Button>
      </CardContent>
    </Card>
  );
};

// Group stage — ALL group matches scoreable in any order, grouped by round.
// Rounds are shown only as organising headings; every match is scoreable
// regardless of which round it belongs to. Group-knockout group phase only.
const GroupStageCard = ({ tournament, matches, teamById, playerById, onScored, onOverride, onReport, onInputFocus, onInputBlur }) => {
  // Group matches by GROUP label first (A, B, C, …), then by round
  // within each group. Falls back to a single "_" bucket for
  // legacy/edge data missing a group tag.
  const byGroup = useMemo(() => {
    const out = {};
    for (const m of matches) {
      const g = m.group || '_';
      const r = m.round || 0;
      out[g] = out[g] || {};
      out[g][r] = out[g][r] || [];
      out[g][r].push(m);
    }
    for (const g of Object.keys(out)) {
      for (const r of Object.keys(out[g])) {
        out[g][r].sort((a, b) => (a.court || 0) - (b.court || 0)
          || (a.match_number || 0) - (b.match_number || 0));
      }
    }
    return out;
  }, [matches]);
  const groupLabels = Object.keys(byGroup).sort();
  const scoreable = matches.filter(m => m.status !== 'bye');
  const scored = scoreable.filter(m => m.status === 'completed').length;

  return (
    <Card className="mb-4" data-testid="group-stage-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm">Group Stage — Matches</CardTitle>
        <span className="text-xs text-gray-500" data-testid="group-stage-progress">{scored} of {scoreable.length} scored</span>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="text-[11px] text-gray-500 -mt-1" data-testid="group-stage-hint">
          Score any match in any order. The knockout bracket unlocks once every group match is scored.
        </div>
        {groupLabels.map(g => {
          const rounds = Object.keys(byGroup[g]).map(Number).sort((a, b) => a - b);
          const gMatches = rounds.flatMap(r => byGroup[g][r]);
          const gScored = gMatches.filter(m => m.status === 'completed').length;
          const gTotal = gMatches.filter(m => m.status !== 'bye').length;
          return (
            <div key={g} data-testid={`group-section-${g}`}>
              <div className="flex items-baseline justify-between mb-2">
                <div className="text-sm font-semibold text-gray-900" data-testid={`group-heading-${g}`}>
                  {g === '_' ? 'Matches' : `Group ${g}`}
                </div>
                <div className="text-[11px] text-gray-500" data-testid={`group-progress-${g}`}>
                  {gScored} of {gTotal} scored
                </div>
              </div>
              {rounds.map(r => (
                <div key={r} className="mb-3">
                  {rounds.length > 1 && (
                    <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1"
                         data-testid={`group-${g}-round-heading-${r}`}>
                      Round {r}
                    </div>
                  )}
                  <div className="space-y-2">
                    {byGroup[g][r].map(m => (
                      <MatchCard
                        key={m.id}
                        tournament={tournament}
                        match={m}
                        team1={teamById[m.team1_id]}
                        team2={teamById[m.team2_id]}
                        playerById={playerById}
                        onScored={onScored}
                        onOverride={onOverride}
                        onReport={onReport}
                        onInputFocus={onInputFocus}
                        onInputBlur={onInputBlur}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

// Current round matches w/ inline score entry
const CurrentRoundCard = ({ tournament, matches, teamById, playerById, onScored, onOverride, onReport, onInputFocus, onInputBlur }) => {
  const byCourt = useMemo(() => {
    const arr = [...matches].sort((a, b) => (a.court || 0) - (b.court || 0) || a.match_number - b.match_number);
    return arr;
  }, [matches]);

  return (
    <Card className="mb-4" data-testid="current-round-card">
      <CardHeader className="pb-2"><CardTitle className="text-sm">{getRoundName(tournament, tournament.current_round, byCourt[0])} — Matches</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        {byCourt.length === 0 ? (
          <div className="text-xs text-gray-500 py-3 text-center">No matches in current round.</div>
        ) : byCourt.map(m => (
          <MatchCard
            key={m.id}
            tournament={tournament}
            match={m}
            team1={teamById[m.team1_id]}
            team2={teamById[m.team2_id]}
            playerById={playerById}
            onScored={onScored}
            onOverride={onOverride}
            onReport={onReport}
            onInputFocus={onInputFocus}
            onInputBlur={onInputBlur}
          />
        ))}
      </CardContent>
    </Card>
  );
};

// FIX 4: Read-only listing of past-round matches, each with an Edit Score button
const PastRoundsCard = ({ tournament, allMatches, currentRound, teamById, playerById, onOverride, onReport }) => {
  const [expanded, setExpanded] = useState(false);
  // FIX 5: when tournament is completed, include the current (final) round
  // in past rounds — otherwise the final match disappears from the UI.
  const isCompleted = tournament?.status === 'completed';
  const cutoff = isCompleted ? currentRound + 1 : currentRound;
  const rounds = useMemo(() => {
    const out = {};
    for (const m of allMatches) {
      if ((m.round || 0) >= cutoff) continue;
      // Only list matches that are completed or were byes — skip empty
      // placeholders from upstream-pending matches in DE.
      if (!['completed', 'bye'].includes(m.status)) continue;
      (out[m.round] = out[m.round] || []).push(m);
    }
    for (const r of Object.keys(out)) {
      out[r].sort((a, b) => (a.court || 0) - (b.court || 0) || (a.match_number || 0) - (b.match_number || 0));
    }
    return out;
  }, [allMatches, cutoff]);
  const roundNums = Object.keys(rounds).map(Number).sort((a, b) => b - a);
  if (roundNums.length === 0) return null;

  return (
    <Card className="mb-4" data-testid="past-rounds-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm">Past rounds ({roundNums.length})</CardTitle>
        <Button size="sm" variant="ghost" onClick={() => setExpanded(e => !e)} data-testid="past-rounds-toggle">
          {expanded ? 'Hide' : 'Show'}
        </Button>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-4">
          {roundNums.map(r => (
            <div key={r}>
              <div className="text-xs uppercase font-semibold text-gray-500 mb-2">{getRoundName(tournament, r, rounds[r][0])}</div>
              <div className="space-y-2">
                {rounds[r].map(m => (
                  <CompletedMatchRow
                    key={m.id}
                    tournament={tournament}
                    match={m}
                    team1={teamById[m.team1_id]}
                    team2={teamById[m.team2_id]}
                    playerById={playerById}
                    onOverride={onOverride}
                    onReport={onReport}
                  />
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      )}
    </Card>
  );
};

// Compact row for past-round matches — read-only score + Edit Score button + Edited badge
const CompletedMatchRow = ({ tournament, match, team1, team2, playerById, onOverride, onReport }) => {
  const team1Label = resolveMatchSide({ playerIds: match.team1_player_ids, team: team1, teamId: match.team1_id }, playerById, { [match.team1_id]: team1 });
  const team2Label = resolveMatchSide({ playerIds: match.team2_player_ids, team: team2, teamId: match.team2_id }, playerById, { [match.team2_id]: team2 });
  const isCompleted = match.status === 'completed';
  const isBye = match.status === 'bye';
  const wasEdited = !!match.overridden_by;
  const isFootball = tournament?.sport === 'football';
  const hasReport = !!match.match_report;

  return (
    <div className={`border rounded-lg p-3 text-sm ${isBye ? 'bg-gray-50 border-gray-200' : 'bg-emerald-50/40 border-emerald-200'}`} data-testid={`past-match-row-${match.id}`}>
      <div className="flex items-center justify-between mb-1.5 flex-wrap gap-1">
        <div className="text-xs text-gray-500">
          {match.bracket && match.bracket !== 'main' && <span className="font-semibold text-blue-700 mr-2 capitalize">{match.bracket.replace('_', ' ')}</span>}
          {match.court && <span>Court {match.court}</span>}
          {match.group && <span className="ml-2">Group {match.group}</span>}
          <span className="ml-2">Match #{match.match_number}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {wasEdited && (
            <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200" data-testid={`edited-badge-${match.id}`}>Edited</span>
          )}
          {isWalkoverMatch(match) && !isBye && (
            <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200" data-testid={`walkover-pill-${match.id}`}>Walkover</span>
          )}
          {isBye && <span className="text-[10px] uppercase font-semibold text-gray-500">Bye</span>}
        </div>
      </div>
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
        <div className={`font-medium ${match.winner_id === match.team1_id ? 'text-emerald-700' : ''}`}>{team1Label}</div>
        <div className="font-mono font-bold text-base text-center px-2">
          {renderFinalScore(match, tournament)}
        </div>
        <div className={`font-medium text-right ${match.winner_id === match.team2_id ? 'text-emerald-700' : ''}`}>{team2Label}</div>
      </div>
      {isCompleted && !isBye && (
        <div className="flex justify-end gap-2 mt-2">
          {isFootball && (
            <Button size="sm" variant="outline" onClick={() => onReport && onReport(match)} data-testid={`report-btn-${match.id}`}>
              <ClipboardList className="h-3 w-3 mr-1" /> {hasReport ? 'View Report' : 'Add Report'}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => onOverride(match)} data-testid={`edit-score-btn-${match.id}`}>
            <Pencil className="h-3 w-3 mr-1" /> Edit Score
          </Button>
        </div>
      )}
    </div>
  );
};

// FIX ④: tiebreaker label per sport ("Pens" for football, "Super Over" for cricket)
const tiebreakerLabel = (tournament) => {
  const sport = tournament?.sport;
  if (sport === 'football') return 'Pens';
  if (sport === 'cricket') return 'Super Over';
  return 'TB';
};

// Walkover detection — a completed match with no recorded score
// (covers BYE auto-advance and No-Show / opponent-walkover matches).
const isWalkoverMatch = (match) => {
  if (!match) return false;
  const noScore = match.score_team1 == null && match.score_team2 == null;
  return noScore && (match.status === 'completed' || match.status === 'bye');
};

// FIX ④: render the final score with optional tiebreaker suffix.
// e.g. "1 : 1 (Pens: 5-4)" when the match was decided by a shootout.
// Walkover (no-show or BYE) renders an explicit WALKOVER pill instead of "— : —".
const renderFinalScore = (match, tournament) => {
  if (isWalkoverMatch(match)) {
    return (
      <span
        className="inline-block text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200"
        data-testid={`walkover-badge-${match.id}`}
      >
        Walkover
      </span>
    );
  }
  const main = `${match.score_team1 ?? '—'} : ${match.score_team2 ?? '—'}`;
  if (match.tiebreaker_used && match.tiebreaker_score_team1 != null && match.tiebreaker_score_team2 != null) {
    return `${main} (${tiebreakerLabel(tournament)}: ${match.tiebreaker_score_team1}-${match.tiebreaker_score_team2})`;
  }
  return main;
};

// FIX ④: should the tiebreaker UI be shown for this match given the typed score?
// Returns true only when KO-stage match + scores are non-empty and equal.
const tiebreakerRequiredFor = (tournament, match, s1, s2) => {
  if (!tournament || !match) return false;
  if (!isKnockoutStageMatchShared(tournament, match)) return false;
  if (s1 === '' || s2 === '' || s1 == null || s2 == null) return false;
  return parseInt(s1, 10) === parseInt(s2, 10);
};

// ─────────────────────────────────────────────────────────────
// Racquet-sport scoring helpers (client-side mirror of the backend
// resolvers in `services/racquet_scoring.py`). Kept intentionally minimal
// — the backend is the source of truth for validation & winner logic;
// this only decides which UI to render.
// ─────────────────────────────────────────────────────────────
const resolveRacquetStageKey = (tournament, match) => {
  if (match?.group) return 'group';
  const fmt = tournament?.format;
  if (fmt === 'round_robin') return 'round_robin';
  const total = Number(tournament?.total_rounds || tournament?.ko_rounds || 0);
  const rnd = Number(match?.round || 0);
  if (!total || !rnd) return 'default';
  const depthFromFinal = total - rnd;
  return ({ 0: 'final', 1: 'semis', 2: 'quarters', 3: 'r16', 4: 'r32' })[depthFromFinal] || `r${2 ** (depthFromFinal + 1)}`;
};

const resolveRacquetStageConfig = (tournament, match) => {
  const byStage = tournament?.scoring_by_stage;
  if (!byStage) return null;
  const key = resolveRacquetStageKey(tournament, match);
  const cfg = byStage[key] || byStage.default;
  if (!cfg || cfg.mode !== 'games_sets_match') return null;
  return {
    best_of_sets: 3, games_target: 6, win_by_margin: 1,
    tiebreak_at: 6, tiebreak_target: 7, tiebreak_win_by: 2,
    ...cfg,
  };
};

const RacquetScoreModal = ({ tournament, match, cfg, team1Label, team2Label, onClose, onSaved }) => {
  const bestOf = cfg.best_of_sets;
  const [sets, setSets] = useState(() => (
    (match.set_scores && match.set_scores.length)
      ? match.set_scores.map((s, i) => ({ set_no: i + 1, side_a_games: s.side_a_games ?? '', side_b_games: s.side_b_games ?? '', side_a_tb: s.side_a_tb ?? '', side_b_tb: s.side_b_tb ?? '' }))
      : [{ set_no: 1, side_a_games: '', side_b_games: '', side_a_tb: '', side_b_tb: '' }]
  ));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const setField = (idx, key, val) => {
    setSets(prev => prev.map((s, i) => i === idx ? { ...s, [key]: val } : s));
  };
  const addSet = () => sets.length < bestOf && setSets([...sets, { set_no: sets.length + 1, side_a_games: '', side_b_games: '', side_a_tb: '', side_b_tb: '' }]);
  const removeSet = (idx) => setSets(prev => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, set_no: i + 1 })));

  const submit = async () => {
    setErr('');
    // Clean up: strip empty tb fields; parse ints
    const cleaned = sets.map(s => {
      const row = { set_no: s.set_no, side_a_games: parseInt(s.side_a_games, 10) || 0, side_b_games: parseInt(s.side_b_games, 10) || 0 };
      if (s.side_a_tb !== '' && s.side_a_tb != null) row.side_a_tb = parseInt(s.side_a_tb, 10) || 0;
      if (s.side_b_tb !== '' && s.side_b_tb != null) row.side_b_tb = parseInt(s.side_b_tb, 10) || 0;
      return row;
    });
    setSaving(true);
    try {
      await api.request(`/tournaments/admin/${tournament.id}/matches/${match.id}/override-score`, {
        method: 'POST',
        body: JSON.stringify({ set_scores: cleaned }),
      });
      toast({ title: 'Score saved' });
      onSaved?.();
    } catch (e) {
      setErr(e.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="racquet-score-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
          <h2 className="font-semibold text-sm">Enter set scores</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="racquet-close">✕</button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-xs text-gray-600">
            Best of {bestOf} · race to {cfg.games_target}{cfg.tiebreak_at != null ? ` · tiebreak at ${cfg.tiebreak_at}-${cfg.tiebreak_at} (first to ${cfg.tiebreak_target}, win by ${cfg.tiebreak_win_by})` : ` · win by ${cfg.win_by_margin}`}
          </div>
          <div className="text-xs font-medium text-gray-900 grid grid-cols-[auto_1fr_auto_1fr_auto] items-center gap-2">
            <span></span><span className="text-right">{team1Label}</span><span className="px-2">—</span><span>{team2Label}</span><span></span>
          </div>
          {sets.map((s, i) => {
            const showTb = cfg.tiebreak_at != null && Number(s.side_a_games) === cfg.tiebreak_at && Number(s.side_b_games) === cfg.tiebreak_at;
            return (
              <div key={i} className="border rounded-lg p-2 space-y-1.5" data-testid={`racquet-set-${i + 1}`}>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-700 w-14">Set {s.set_no}</span>
                  <Input type="number" min="0" max="99" value={s.side_a_games} onChange={(e) => setField(i, 'side_a_games', e.target.value)} className="w-14 text-center" data-testid={`set-${i + 1}-a-games`} />
                  <span className="text-gray-400">—</span>
                  <Input type="number" min="0" max="99" value={s.side_b_games} onChange={(e) => setField(i, 'side_b_games', e.target.value)} className="w-14 text-center" data-testid={`set-${i + 1}-b-games`} />
                  {sets.length > 1 && (
                    <button onClick={() => removeSet(i)} className="text-xs text-red-500 hover:underline ml-auto" data-testid={`set-${i + 1}-remove`}>remove</button>
                  )}
                </div>
                {showTb && (
                  <div className="flex items-center gap-2 pl-14" data-testid={`set-${i + 1}-tb-row`}>
                    <span className="text-[11px] text-indigo-700">Tiebreak:</span>
                    <Input type="number" min="0" max="99" value={s.side_a_tb} onChange={(e) => setField(i, 'side_a_tb', e.target.value)} className="w-14 text-center" data-testid={`set-${i + 1}-a-tb`} placeholder="TB" />
                    <span className="text-gray-400">—</span>
                    <Input type="number" min="0" max="99" value={s.side_b_tb} onChange={(e) => setField(i, 'side_b_tb', e.target.value)} className="w-14 text-center" data-testid={`set-${i + 1}-b-tb`} placeholder="TB" />
                  </div>
                )}
              </div>
            );
          })}
          {sets.length < bestOf && (
            <button onClick={addSet} className="w-full py-1.5 border border-dashed rounded-lg text-xs text-gray-600 hover:bg-gray-50" data-testid="racquet-add-set">+ Add set {sets.length + 1}</button>
          )}
          {err && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5">{err}</div>}
        </div>
        <div className="sticky bottom-0 bg-white border-t px-4 py-3 flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1" data-testid="racquet-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="flex-1" data-testid="racquet-submit">
            {saving ? 'Saving…' : 'Save score'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Small inline control shown in the match-card header: `Court N`
// label doubles as a "change court" affordance. Admin can enter ANY
// positive court number — this is an explicit override that bypasses
// the auto-spread cap of `num_courts`, per admin request (some
// venues have more physical courts than num_courts suggests).
const MatchCourtControl = ({ tournament, match, onChanged }) => {
  const isCompleted = match.status === 'completed';
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(String(match.court || 1));

  useEffect(() => { setDraft(String(match.court || 1)); }, [match.court]);

  const commit = async (raw) => {
    const next = parseInt(raw, 10);
    if (!next || next < 1 || next === match.court) {
      setDraft(String(match.court || 1));
      return;
    }
    setSaving(true);
    try {
      await api.request(
        `/tournaments/admin/${tournament.id}/matches/${match.id}/court`,
        { method: 'PATCH', body: JSON.stringify({ court: next }) },
      );
      toast({ title: `Moved to Court ${next}` });
      onChanged?.();
    } catch (err) {
      toast({ title: 'Could not change court', description: err.message, variant: 'destructive' });
      setDraft(String(match.court || 1));
    } finally { setSaving(false); }
  };

  if (isCompleted) return <span data-testid={`match-court-${match.id}`}>Court {match.court}</span>;
  return (
    <label className="inline-flex items-center gap-1" data-testid={`match-court-control-${match.id}`}>
      Court
      <input
        type="number"
        min="1"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={e => commit(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        disabled={saving}
        className="border border-gray-200 rounded px-1 py-0.5 text-xs bg-white hover:bg-gray-50 w-12"
        data-testid={`match-court-select-${match.id}`}
      />
    </label>
  );
};



const MatchCard = ({ tournament, match, team1, team2, playerById, onScored, onOverride, onReport, onInputFocus, onInputBlur }) => {
  const [s1, setS1] = useState(match.score_team1 ?? '');
  const [s2, setS2] = useState(match.score_team2 ?? '');
  const [tb1, setTb1] = useState('');
  const [tb2, setTb2] = useState('');
  const [saving, setSaving] = useState(false);
  const [racquetOpen, setRacquetOpen] = useState(false);

  const team1Label = resolveMatchSide({ playerIds: match.team1_player_ids, team: team1, teamId: match.team1_id }, playerById, { [match.team1_id]: team1 });
  const team2Label = resolveMatchSide({ playerIds: match.team2_player_ids, team: team2, teamId: match.team2_id }, playerById, { [match.team2_id]: team2 });

  const isCompleted = match.status === 'completed';
  const isBye = match.status === 'bye';
  const hasTeams = !!match.team1_id && !!match.team2_id;
  const needsTb = tiebreakerRequiredFor(tournament, match, s1, s2);
  const racquetCfg = resolveRacquetStageConfig(tournament, match);
  const isRacquetMatch = !!racquetCfg;

  const submit = async () => {
    if (s1 === '' || s2 === '') return;
    if (needsTb && (tb1 === '' || tb2 === '' || parseInt(tb1, 10) === parseInt(tb2, 10))) {
      toast({ title: 'Tiebreaker required', description: 'Enter a non-tied tiebreaker score', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const body = {
        score_team1: parseInt(s1, 10),
        score_team2: parseInt(s2, 10),
      };
      if (needsTb) {
        body.tiebreaker_used = true;
        body.tiebreaker_score_team1 = parseInt(tb1, 10);
        body.tiebreaker_score_team2 = parseInt(tb2, 10);
      }
      await api.request(`/tournaments/admin/${tournament.id}/matches/${match.id}/override-score`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      toast({ title: 'Score saved' });
      await onScored();
    } catch (e) {
      toast({ title: 'Could not save', description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div className={`border rounded-lg p-3 ${isCompleted ? 'bg-emerald-50/50 border-emerald-200' : isBye ? 'bg-gray-50 border-gray-200' : 'bg-white'}`} data-testid={`match-card-${match.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-gray-500 flex items-center gap-2">
          {match.bracket && match.bracket !== 'main' && <span className="font-semibold text-blue-700 mr-1 capitalize">{match.bracket.replace('_', ' ')}</span>}
          {match.court && (
            <MatchCourtControl
              tournament={tournament}
              match={match}
              onChanged={onScored}
            />
          )}
          {match.group && <span>Group {match.group}</span>}
          <span>Match #{match.match_number}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {match.overridden_by && (
            <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200" data-testid={`edited-badge-${match.id}`}>Edited</span>
          )}
          {isCompleted && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
          {isWalkoverMatch(match) && !isBye && (
            <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200" data-testid={`walkover-pill-${match.id}`}>Walkover</span>
          )}
          {isBye && <span className="text-[10px] uppercase font-semibold text-gray-500">Bye</span>}
        </div>
      </div>

      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
        <div className="text-sm">
          <div className={`font-medium ${match.winner_id === match.team1_id ? 'text-emerald-700' : ''}`}>{team1Label}</div>
        </div>
        {/* FIX 1: when match is completed, show the persisted score as static text
            instead of using local input state. Local state only initialises on mount
            and never picks up the referee's score until manual refresh — switching to
            static text removes the race condition entirely. */}
        {isCompleted ? (
          <div className="font-mono font-bold text-base text-center px-2" data-testid={`match-final-score-${match.id}`}>
            {renderFinalScore(match, tournament)}
          </div>
        ) : isBye ? (
          <div className="text-[11px] uppercase font-semibold text-gray-500 px-2">Walkover</div>
        ) : isRacquetMatch ? (
          <div className="text-[11px] text-indigo-700 px-2 text-center" data-testid={`racquet-hint-${match.id}`}>
            Bo{racquetCfg.best_of_sets} · to {racquetCfg.games_target}<br />
            <span className="text-gray-500">tap "Enter set scores"</span>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <input type="number" min="0" value={s1} disabled={!hasTeams}
              onChange={e => setS1(e.target.value)}
              onFocus={onInputFocus} onBlur={onInputBlur}
              className="w-14 px-2 py-1 border rounded text-center text-sm" placeholder="-"
              data-testid={`score-input-team1-${match.id}`} />
            <span className="text-gray-400">:</span>
            <input type="number" min="0" value={s2} disabled={!hasTeams}
              onChange={e => setS2(e.target.value)}
              onFocus={onInputFocus} onBlur={onInputBlur}
              className="w-14 px-2 py-1 border rounded text-center text-sm" placeholder="-"
              data-testid={`score-input-team2-${match.id}`} />
          </div>
        )}
        <div className="text-sm text-right">
          <div className={`font-medium ${match.winner_id === match.team2_id ? 'text-emerald-700' : ''}`}>{team2Label}</div>
        </div>
      </div>

      {/* FIX ④: KO-stage tied score requires a tiebreaker */}
      {needsTb && !isCompleted && (
        <div className="mt-2 p-2 rounded-md bg-amber-50 border border-amber-200" data-testid={`tiebreaker-row-${match.id}`}>
          <div className="text-[11px] uppercase font-semibold text-amber-900 mb-1">
            {tiebreakerLabel(tournament)} required — score is tied
          </div>
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
            <div className="text-xs text-amber-900 truncate">{team1Label}</div>
            <div className="flex items-center gap-1">
              <input type="number" min="0" value={tb1}
                onChange={e => setTb1(e.target.value)}
                onFocus={onInputFocus} onBlur={onInputBlur}
                className="w-14 px-2 py-1 border rounded text-center text-sm" placeholder="-"
                data-testid={`tiebreaker-input-team1-${match.id}`} />
              <span className="text-gray-400">:</span>
              <input type="number" min="0" value={tb2}
                onChange={e => setTb2(e.target.value)}
                onFocus={onInputFocus} onBlur={onInputBlur}
                className="w-14 px-2 py-1 border rounded text-center text-sm" placeholder="-"
                data-testid={`tiebreaker-input-team2-${match.id}`} />
            </div>
            <div className="text-xs text-amber-900 text-right truncate">{team2Label}</div>
          </div>
        </div>
      )}

      {!hasTeams && !isBye && (
        <div className="text-[11px] text-amber-600 mt-2">Awaiting upstream result…</div>
      )}

      <div className="flex justify-end gap-2 mt-2">
        {isCompleted ? (
          <>
            {tournament?.sport === 'football' && (
              <Button size="sm" variant="outline" onClick={() => onReport && onReport(match)} data-testid={`report-btn-${match.id}`}>
                <ClipboardList className="h-3 w-3 mr-1" /> {match.match_report ? 'View Report' : 'Add Report'}
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => isRacquetMatch ? setRacquetOpen(true) : onOverride(match)} data-testid={`edit-score-btn-${match.id}`}>
              <Pencil className="h-3 w-3 mr-1" /> Edit Score
            </Button>
          </>
        ) : hasTeams && !isBye && (
          isRacquetMatch ? (
            <Button size="sm" onClick={() => setRacquetOpen(true)} data-testid={`enter-set-scores-${match.id}`}>
              Enter set scores
            </Button>
          ) : (
            <Button size="sm" onClick={submit} disabled={s1 === '' || s2 === '' || saving} data-testid={`save-score-btn-${match.id}`}>
              {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              Save score
            </Button>
          )
        )}
      </div>
      {racquetOpen && racquetCfg && (
        <RacquetScoreModal
          tournament={tournament}
          match={match}
          cfg={racquetCfg}
          team1Label={team1Label}
          team2Label={team2Label}
          onClose={() => setRacquetOpen(false)}
          onSaved={async () => { setRacquetOpen(false); await onScored(); }}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Top Scorers card (football only)
// ─────────────────────────────────────────────────────────────
const TopScorersCard = ({ tournamentId, refreshKey }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.request(`/tournaments/admin/${tournamentId}/top-scorers`);
        if (alive) setRows(Array.isArray(data) ? data : []);
      } catch {
        if (alive) setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [tournamentId, refreshKey]);

  return (
    <Card className="mb-4" data-testid="top-scorers-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">⚽ Top Scorers</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-4 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-gray-400" /></div>
        ) : rows.length === 0 ? (
          <div className="text-xs text-gray-500 py-3 text-center" data-testid="top-scorers-empty">No goals recorded yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 border-b">
                  <th className="py-2 pr-2 w-8">#</th>
                  <th className="py-2 pr-2">Player</th>
                  <th className="py-2 pr-2">Team</th>
                  <th className="py-2 px-2 text-center">Goals</th>
                  <th className="py-2 px-2 text-center">🟨</th>
                  <th className="py-2 pl-2 text-center">🟥</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.player_id || r.player_name}-${r.team_id}`} className="border-b last:border-0" data-testid={`top-scorer-row-${i}`}>
                    <td className="py-2 pr-2 text-gray-400 font-mono">{i + 1}</td>
                    <td className="py-2 pr-2 font-medium text-gray-900" data-testid={`top-scorer-name-${i}`}>{r.player_name}</td>
                    <td className="py-2 pr-2 text-gray-600">{r.team_name}</td>
                    <td className="py-2 px-2 text-center font-bold text-emerald-700" data-testid={`top-scorer-goals-${i}`}>{r.total_goals}</td>
                    <td className="py-2 px-2 text-center text-gray-700">{r.yellow_cards || 0}</td>
                    <td className="py-2 pl-2 text-center text-gray-700">{r.red_cards || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────
// Match Report dialog (admin) — football
// ─────────────────────────────────────────────────────────────
const adminBlankGoal = () => ({ player_name: '', count: 1 });
const adminBlankCard = (teamId) => ({ player_name: '', team_id: teamId });

const MatchReportDialog = ({ tournamentId, match, team1, team2, onClose, onSaved }) => {
  const hasReport = !!match.match_report;
  const [editing, setEditing] = useState(!hasReport);
  const [saving, setSaving] = useState(false);
  const [warning, setWarning] = useState(null);

  const t1Id = match.team1_id, t2Id = match.team2_id;
  const t1Name = team1?.team_name || 'Team A';
  const t2Name = team2?.team_name || 'Team B';
  const t1Squad = (team1?.players || []).map(p => p.name).filter(Boolean);
  const t2Squad = (team2?.players || []).map(p => p.name).filter(Boolean);
  const squadByTeam = { [t1Id]: t1Squad, [t2Id]: t2Squad };

  const rep = match.match_report;
  const [g1, setG1] = useState(rep ? rep.goals.filter(g => g.team_id === t1Id).map(g => ({ player_name: g.player_name, count: g.count || 1 })) : []);
  const [g2, setG2] = useState(rep ? rep.goals.filter(g => g.team_id === t2Id).map(g => ({ player_name: g.player_name, count: g.count || 1 })) : []);
  const [yellows, setYellows] = useState(rep ? rep.yellow_cards.map(c => ({ player_name: c.player_name, team_id: c.team_id })) : []);
  const [reds, setReds] = useState(rep ? rep.red_cards.map(c => ({ player_name: c.player_name, team_id: c.team_id })) : []);

  const resolveId = (teamId, name) => {
    const team = teamId === t1Id ? team1 : team2;
    const hit = (team?.players || []).find(p => (p.name || '').trim().toLowerCase() === (name || '').trim().toLowerCase());
    return hit?.id || null;
  };

  const buildBody = () => {
    const goals = [];
    g1.filter(r => r.player_name.trim()).forEach(r => goals.push({ player_name: r.player_name.trim(), player_id: resolveId(t1Id, r.player_name), team_id: t1Id, count: Math.max(1, parseInt(r.count, 10) || 1) }));
    g2.filter(r => r.player_name.trim()).forEach(r => goals.push({ player_name: r.player_name.trim(), player_id: resolveId(t2Id, r.player_name), team_id: t2Id, count: Math.max(1, parseInt(r.count, 10) || 1) }));
    const yellow_cards = yellows.filter(c => c.player_name.trim()).map(c => ({ player_name: c.player_name.trim(), player_id: resolveId(c.team_id, c.player_name), team_id: c.team_id }));
    const red_cards = reds.filter(c => c.player_name.trim()).map(c => ({ player_name: c.player_name.trim(), player_id: resolveId(c.team_id, c.player_name), team_id: c.team_id }));
    return { goals, yellow_cards, red_cards };
  };

  const save = async () => {
    setSaving(true); setWarning(null);
    try {
      const r = await api.request(`/tournaments/admin/${tournamentId}/matches/${match.id}/report`, {
        method: hasReport ? 'PATCH' : 'POST',
        body: JSON.stringify(buildBody()),
      });
      if (r?.warning) {
        setWarning(r.warning);
        toast({ title: 'Report saved with a warning', description: r.warning, variant: 'destructive' });
      } else {
        toast({ title: 'Match report saved' });
      }
      onSaved();
    } catch (e) {
      toast({ title: 'Could not save report', description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const scoreLine = `${match.score_team1 ?? '—'} : ${match.score_team2 ?? '—'}`;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="match-report-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Match Report</DialogTitle>
          <DialogDescription className="text-xs">
            {t1Name} <span className="font-mono font-bold mx-1">{scoreLine}</span> {t2Name}
          </DialogDescription>
        </DialogHeader>

        {warning && (
          <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="report-warning-banner">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> {warning}
          </div>
        )}

        {!editing && rep ? (
          <ReportReadOnly rep={rep} t1Id={t1Id} t2Id={t2Id} t1Name={t1Name} t2Name={t2Name} onEdit={() => setEditing(true)} />
        ) : (
          <div className="space-y-3" data-testid="report-edit-form">
            <AdminGoalTeam label={t1Name} rows={g1} setRows={setG1} suggestions={t1Squad} testid="report-g1" />
            <AdminGoalTeam label={t2Name} rows={g2} setRows={setG2} suggestions={t2Squad} testid="report-g2" />
            <AdminCardSection title="Yellow cards" dot="bg-amber-400" rows={yellows} setRows={setYellows}
              t1={{ id: t1Id, name: t1Name }} t2={{ id: t2Id, name: t2Name }} squadByTeam={squadByTeam} defaultTeam={t1Id} testid="report-yellow" />
            <AdminCardSection title="Red cards" dot="bg-red-500" rows={reds} setRows={setReds}
              t1={{ id: t1Id, name: t1Name }} t2={{ id: t2Id, name: t2Name }} squadByTeam={squadByTeam} defaultTeam={t1Id} testid="report-red" />
          </div>
        )}

        <DialogFooter>
          {!editing && rep ? (
            <Button variant="outline" onClick={onClose} data-testid="report-close-btn">Close</Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} data-testid="report-cancel-btn">Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="report-save-btn">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save Report
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ReportReadOnly = ({ rep, t1Id, t2Id, t1Name, t2Name, onEdit }) => {
  const goalsFor = (tid) => rep.goals.filter(g => g.team_id === tid);
  const cardList = (arr) => arr.length ? arr.map((c, i) => <span key={i} className="inline-block px-2 py-0.5 bg-gray-100 rounded mr-1 mb-1 text-xs">{c.player_name} <span className="text-gray-400">({c.team_id === t1Id ? t1Name : t2Name})</span></span>) : <span className="text-xs text-gray-400">None</span>;
  return (
    <div className="space-y-3" data-testid="report-readonly">
      {[{ id: t1Id, name: t1Name }, { id: t2Id, name: t2Name }].map(t => (
        <div key={t.id}>
          <div className="text-sm font-bold text-gray-800">⚽ {t.name}</div>
          {goalsFor(t.id).length ? goalsFor(t.id).map((g, i) => (
            <div key={i} className="text-sm text-gray-700 ml-4">{g.player_name} {g.count > 1 ? <span className="font-bold text-emerald-700">×{g.count}</span> : ''}</div>
          )) : <div className="text-xs text-gray-400 ml-4">No goals</div>}
        </div>
      ))}
      <div>
        <div className="text-sm font-bold text-gray-800 flex items-center gap-1.5"><span className="inline-block w-3 h-4 rounded-sm bg-amber-400" /> Yellow cards</div>
        <div className="ml-4 mt-1">{cardList(rep.yellow_cards)}</div>
      </div>
      <div>
        <div className="text-sm font-bold text-gray-800 flex items-center gap-1.5"><span className="inline-block w-3 h-4 rounded-sm bg-red-500" /> Red cards</div>
        <div className="ml-4 mt-1">{cardList(rep.red_cards)}</div>
      </div>
      <div className="text-[11px] text-gray-400 border-t pt-2">
        Reported by {rep.reported_by} · {rep.reported_at ? new Date(rep.reported_at).toLocaleString() : ''}
        {rep.last_edited_by && <> · Edited by {rep.last_edited_by}</>}
      </div>
      <Button size="sm" variant="outline" onClick={onEdit} data-testid="report-edit-btn"><Pencil className="h-3 w-3 mr-1" /> Edit</Button>
    </div>
  );
};

const AdminGoalTeam = ({ label, rows, setRows, suggestions, testid }) => {
  const update = (i, patch) => setRows(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const remove = (i) => setRows(rows.filter((_, idx) => idx !== i));
  return (
    <div data-testid={testid}>
      <div className="text-sm font-bold text-gray-800 mb-1">⚽ {label}</div>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i}>
            <div className="flex items-center gap-2">
              <input value={r.player_name} onChange={e => update(i, { player_name: e.target.value })} placeholder="Player name"
                className="flex-1 min-w-0 px-2 py-1.5 border rounded text-sm" data-testid={`${testid}-name-${i}`} />
              <button onClick={() => update(i, { count: Math.max(1, (parseInt(r.count, 10) || 1) - 1) })} className="h-7 w-7 rounded border flex items-center justify-center" aria-label="dec"><Minus className="h-3 w-3" /></button>
              <span className="w-6 text-center font-bold text-sm" data-testid={`${testid}-count-${i}`}>{r.count}</span>
              <button onClick={() => update(i, { count: (parseInt(r.count, 10) || 1) + 1 })} className="h-7 w-7 rounded border flex items-center justify-center" aria-label="inc"><Plus className="h-3 w-3" /></button>
              <button onClick={() => remove(i)} className="text-gray-400 h-7 w-7 flex items-center justify-center" aria-label="remove"><X className="h-4 w-4" /></button>
            </div>
            {suggestions.length > 0 && !r.player_name && (
              <div className="flex flex-wrap gap-1 mt-1">
                {suggestions.map((name, si) => (
                  <button key={si} onClick={() => update(i, { player_name: name })} className="text-xs px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700" data-testid={`${testid}-sug-${i}-${si}`}>{name}</button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <button onClick={() => setRows([...rows, adminBlankGoal()])} className="mt-1.5 flex items-center gap-1 text-xs font-semibold text-blue-700" data-testid={`${testid}-add`}><Plus className="h-3 w-3" /> Add scorer</button>
    </div>
  );
};

const AdminCardSection = ({ title, dot, rows, setRows, t1, t2, squadByTeam, defaultTeam, testid }) => {
  const update = (i, patch) => setRows(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const remove = (i) => setRows(rows.filter((_, idx) => idx !== i));
  return (
    <div data-testid={testid}>
      <div className="text-sm font-bold text-gray-800 mb-1 flex items-center gap-1.5"><span className={`inline-block w-3 h-4 rounded-sm ${dot}`} /> {title}</div>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2">
            <input value={r.player_name} onChange={e => update(i, { player_name: e.target.value })} placeholder="Player name"
              className="flex-1 min-w-0 px-2 py-1.5 border rounded text-sm" data-testid={`${testid}-name-${i}`} />
            <select value={r.team_id} onChange={e => update(i, { team_id: e.target.value })} className="px-1.5 py-1.5 border rounded text-xs bg-white max-w-[38%] truncate" data-testid={`${testid}-team-${i}`}>
              <option value={t1.id}>{t1.name}</option>
              <option value={t2.id}>{t2.name}</option>
            </select>
            <button onClick={() => remove(i)} className="text-gray-400 h-7 w-7 flex items-center justify-center" aria-label="remove"><X className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <button onClick={() => setRows([...rows, adminBlankCard(defaultTeam)])} className="mt-1.5 flex items-center gap-1 text-xs font-semibold text-blue-700" data-testid={`${testid}-add`}><Plus className="h-3 w-3" /> Add card</button>
    </div>
  );
};

// Score override dialog with confirmation
const ScoreOverrideDialog = ({ tournamentId, tournament, match, team1Name, team2Name, onClose, onSaved }) => {
  const [s1, setS1] = useState(match.score_team1 ?? 0);
  const [s2, setS2] = useState(match.score_team2 ?? 0);
  const [tb1, setTb1] = useState(match.tiebreaker_score_team1 ?? '');
  const [tb2, setTb2] = useState(match.tiebreaker_score_team2 ?? '');
  const [saving, setSaving] = useState(false);
  const needsTb = tiebreakerRequiredFor(tournament, match, s1, s2);

  const submit = async () => {
    if (needsTb && (tb1 === '' || tb2 === '' || parseInt(tb1, 10) === parseInt(tb2, 10))) {
      toast({ title: 'Tiebreaker required', description: 'Enter a non-tied tiebreaker score', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const body = {
        score_team1: parseInt(s1, 10),
        score_team2: parseInt(s2, 10),
      };
      if (needsTb) {
        body.tiebreaker_used = true;
        body.tiebreaker_score_team1 = parseInt(tb1, 10);
        body.tiebreaker_score_team2 = parseInt(tb2, 10);
      } else {
        // If admin edited away from a tie, clear any old tiebreaker.
        body.tiebreaker_used = false;
        body.tiebreaker_score_team1 = null;
        body.tiebreaker_score_team2 = null;
      }
      await api.request(`/tournaments/admin/${tournamentId}/matches/${match.id}/override-score`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      toast({ title: 'Score updated successfully' });
      onSaved();
    } catch (e) {
      toast({ title: 'Could not update score', description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="override-dialog">
        <DialogHeader>
          <DialogTitle>Edit match score</DialogTitle>
          <DialogDescription>This action is audited — the match will be marked as Edited and visible in the match history.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-gray-50 border rounded-lg p-3 text-sm">
            <div className="text-xs text-gray-500 mb-1">Current (committed) score</div>
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
              <div className="font-medium">{team1Name}</div>
              <div className="font-mono font-bold text-lg">{match.score_team1} : {match.score_team2}</div>
              <div className="font-medium text-right">{team2Name}</div>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
            <div className="text-xs text-amber-700 font-semibold mb-1">New score</div>
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
              <div className="font-medium">{team1Name}</div>
              <div className="flex items-center gap-1 justify-center">
                <input type="number" min="0" value={s1} onChange={e => setS1(e.target.value)}
                  className="w-16 px-2 py-1 border rounded text-center text-lg font-bold"
                  data-testid="override-s1" />
                <span>:</span>
                <input type="number" min="0" value={s2} onChange={e => setS2(e.target.value)}
                  className="w-16 px-2 py-1 border rounded text-center text-lg font-bold"
                  data-testid="override-s2" />
              </div>
              <div className="font-medium text-right">{team2Name}</div>
            </div>
          </div>
          {/* FIX ④: tiebreaker inputs in Edit Score for KO-stage ties */}
          {needsTb && (
            <div className="bg-amber-100 border border-amber-300 rounded-lg p-3 text-sm" data-testid="override-tiebreaker-row">
              <div className="text-xs text-amber-800 font-semibold mb-1">
                {tiebreakerLabel(tournament)} (score is tied) *
              </div>
              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                <div className="text-xs">{team1Name}</div>
                <div className="flex items-center gap-1 justify-center">
                  <input type="number" min="0" value={tb1} onChange={e => setTb1(e.target.value)}
                    className="w-16 px-2 py-1 border rounded text-center text-lg font-bold"
                    data-testid="override-tb1" />
                  <span>:</span>
                  <input type="number" min="0" value={tb2} onChange={e => setTb2(e.target.value)}
                    className="w-16 px-2 py-1 border rounded text-center text-lg font-bold"
                    data-testid="override-tb2" />
                </div>
                <div className="text-xs text-right">{team2Name}</div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="override-confirm-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Confirm edit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Individual leaderboard (americano/mexicano/mixed/king) — # | Player | W | T | L | P | +/-
const LeaderboardCard = ({ format, standings, teamById, playerById }) => {
  const isKing = format === 'king_of_court';
  const ranked = useMemo(() => {
    // Americano family: P DESC → +/- DESC → W DESC. King: rounds-on-court-1 primary.
    const cmp = (a, b) => {
      if (isKing && (b.rounds_on_court_1 || 0) !== (a.rounds_on_court_1 || 0)) {
        return (b.rounds_on_court_1 || 0) - (a.rounds_on_court_1 || 0);
      }
      return (b.cumulative_score || 0) - (a.cumulative_score || 0)
        || (b.plus_minus || 0) - (a.plus_minus || 0)
        || (b.won || 0) - (a.won || 0);
    };
    return [...standings].sort(cmp);
  }, [standings, isKing]);
  const fmtPM = (v) => `${(v || 0) > 0 ? '+' : ''}${v || 0}`;

  return (
    <Card className="mb-4" data-testid="leaderboard-card">
      <CardHeader className="pb-2"><CardTitle className="text-sm">Leaderboard{isKing ? ' (Rounds on Court 1)' : ''}</CardTitle></CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-xs text-gray-500 border-b">
              <th className="text-left py-1.5">#</th>
              <th className="text-left">Player</th>
              {isKing && <th className="text-right">Rds&nbsp;C1</th>}
              <th className="text-right">W</th>
              <th className="text-right">T</th>
              <th className="text-right">L</th>
              <th className="text-right">P</th>
              <th className="text-right">+/-</th>
            </tr></thead>
            <tbody>
              {ranked.map((r, i) => (
                <tr key={r.id} className="border-b last:border-0" data-testid={`leaderboard-row-${i}`}>
                  <td className="py-1.5 font-mono text-gray-500">{i + 1}</td>
                  <td className="font-medium" data-testid={`leaderboard-name-${i}`}>{resolveEntityName(r.team_id, playerById, teamById)}</td>
                  {isKing && <td className="text-right font-semibold">{r.rounds_on_court_1 || 0}</td>}
                  <td className="text-right">{r.won || 0}</td>
                  <td className="text-right">{r.drawn || 0}</td>
                  <td className="text-right">{r.lost || 0}</td>
                  <td className="text-right font-semibold">{r.cumulative_score || 0}</td>
                  <td className={`text-right ${(r.plus_minus || 0) < 0 ? 'text-red-600' : 'text-gray-700'}`}>{fmtPM(r.plus_minus)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// Team standings (round_robin / group_knockout)
const TeamStandingsCard = ({ tournament, standings, teamById }) => {
  const labels = forAgainstLabels(tournament?.sport);
  const isRacquet = ['padel', 'pickleball', 'tennis'].includes(tournament?.sport);
  const groups = useMemo(() => {
    const out = {};
    for (const s of standings) {
      const g = s.group || '_';
      (out[g] = out[g] || []).push(s);
    }
    // If the backend has stamped `position` (racquet mode with H2H),
    // respect it. Otherwise fall back to the FIFA-style sort.
    const hasPositions = standings.some((s) => typeof s.position === 'number' && s.position > 0);
    for (const g of Object.keys(out)) {
      if (hasPositions) {
        out[g].sort((a, b) => (a.position || 999) - (b.position || 999));
      } else {
        out[g].sort((a, b) => (b.points || 0) - (a.points || 0)
          || (b.goal_difference || 0) - (a.goal_difference || 0)
          || (b.goals_for || 0) - (a.goals_for || 0));
      }
    }
    return out;
  }, [standings]);

  return (
    <Card className="mb-4" data-testid="team-standings-card">
      <CardHeader className="pb-2"><CardTitle className="text-sm">Standings</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {Object.entries(groups).map(([g, rows]) => (
          <div key={g}>
            {g !== '_' && <div className="text-xs font-semibold text-gray-700 mb-1">Group {g}</div>}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-gray-500 border-b">
                  <th className="text-left py-1.5">#</th>
                  <th className="text-left">Team</th>
                  <th className="text-right">P</th><th className="text-right">W</th>
                  <th className="text-right">D</th><th className="text-right">L</th>
                  {isRacquet && <th className="text-right hidden sm:table-cell">SW</th>}
                  {isRacquet && <th className="text-right hidden sm:table-cell">SL</th>}
                  <th className="text-right hidden sm:table-cell">{labels.for}</th>
                  <th className="text-right hidden sm:table-cell">{labels.against}</th>
                  <th className="text-right">{labels.diff}</th><th className="text-right">Pts</th>
                </tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.id} className="border-b last:border-0">
                      <td className="py-1.5 font-mono text-gray-500">{i + 1}</td>
                      <td className="font-medium">{teamById[r.team_id]?.team_name || r.team_id}</td>
                      <td className="text-right">{r.played || 0}</td>
                      <td className="text-right">{r.won || 0}</td>
                      <td className="text-right">{r.drawn || 0}</td>
                      <td className="text-right">{r.lost || 0}</td>
                      {isRacquet && <td className="text-right hidden sm:table-cell">{r.sets_for || 0}</td>}
                      {isRacquet && <td className="text-right hidden sm:table-cell">{r.sets_against || 0}</td>}
                      <td className="text-right hidden sm:table-cell">{r.goals_for || 0}</td>
                      <td className="text-right hidden sm:table-cell">{r.goals_against || 0}</td>
                      <td className="text-right">{r.goal_difference || 0}</td>
                      <td className="text-right font-semibold">{r.points || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};


// ─────────────────────────────────────────────────────────────
// Teams Management + Check-in (operational; does NOT affect fixtures)
// ─────────────────────────────────────────────────────────────

const formatCheckinTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' });
  } catch { return ''; }
};

const StatusBadge = ({ status, time }) => {
  if (status === 'no_show') {
    return <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200" data-testid="badge-noshow"><X className="h-3 w-3" /> No show</span>;
  }
  if (status === 'checked_in') {
    return <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="badge-checkedin"><CheckCircle2 className="h-3 w-3" /> Checked in{time ? ` · ${time}` : ''}</span>;
  }
  return <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200" data-testid="badge-notarrived"><Loader2 className="h-3 w-3" /> Not arrived</span>;
};

const teamStatus = (t) => {
  if (t?.no_show) return 'no_show';
  if (t?.checked_in) return 'checked_in';
  return 'not_arrived';
};

const TeamsManagementCard = ({ tournament, teams, allMatches, onChanged }) => {
  const { isSuperAdmin } = useAuth();
  const [editTarget, setEditTarget] = useState(null);
  const [noShowTarget, setNoShowTarget] = useState(null);

  const checkedIn = teams.filter(t => t.checked_in).length;
  const noShow = teams.filter(t => t.no_show).length;
  const total = teams.length;
  const notArrived = total - checkedIn - noShow;
  const pct = total > 0 ? Math.round((checkedIn / total) * 100) : 0;

  const isClosed = tournament.status === 'completed' || tournament.status === 'cancelled';

  const checkIn = async (team) => {
    try {
      await api.request(`/tournaments/admin/${tournament.id}/teams/${team.id}/check-in`, { method: 'POST' });
      toast({ title: `${team.team_name} checked in` });
      onChanged();
    } catch (e) {
      toast({ title: 'Check-in failed', description: e.message, variant: 'destructive' });
    }
  };

  const undoCheckIn = async (team) => {
    if (!window.confirm(`Undo check-in for ${team.team_name}? Their team sheet will become editable again.`)) return;
    try {
      await api.request(`/tournaments/admin/${tournament.id}/teams/${team.id}/undo-check-in`, { method: 'POST' });
      toast({ title: 'Check-in reverted' });
      onChanged();
    } catch (e) {
      toast({ title: 'Could not undo', description: e.message, variant: 'destructive' });
    }
  };

  return (
    <Card className="mb-4" data-testid="teams-management-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between">
          <span>Teams</span>
          <span className="text-xs text-gray-500" data-testid="checkin-summary">
            {checkedIn} of {total} checked in{noShow > 0 ? ` · ${noShow} no-show` : ''}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Progress bar */}
        {total > 0 && (
          <div className="space-y-1">
            <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
            {tournament.status === 'active' && notArrived > 0 && (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 flex items-start gap-1.5" data-testid="checkin-warning-banner">
                <AlertCircle className="h-3 w-3 flex-shrink-0 mt-0.5" />
                {notArrived} team{notArrived !== 1 ? 's have' : ' has'} not checked in yet.
              </div>
            )}
          </div>
        )}

        {/* Team rows */}
        {teams.length === 0 ? (
          <div className="text-xs text-gray-500 py-4 text-center">No teams added yet.</div>
        ) : (
          <div className="space-y-2">
            {teams.map(t => {
              const status = teamStatus(t);
              const locked = !!t.team_sheet_locked;
              const isCheckedIn = status === 'checked_in';
              const isNoShow = status === 'no_show';
              return (
                <div key={t.id} className="border rounded-lg px-3 py-2.5 flex flex-wrap items-center justify-between gap-2" data-testid={`team-row-${t.id}`}>
                  <div className="flex flex-col">
                    <EditableTeamName
                      tournamentId={tournament.id}
                      team={t}
                      onSaved={onChanged}
                      disabled={isClosed}
                    />
                    <div className="text-[11px] text-gray-500">{(t.players || []).length} player{(t.players || []).length !== 1 ? 's' : ''}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusBadge status={status} time={isCheckedIn ? formatCheckinTime(t.checked_in_at) : null} />
                    {!isClosed && !isCheckedIn && !isNoShow && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setEditTarget(t)} data-testid={`edit-squad-btn-${t.id}`}>
                          <Pencil className="h-3 w-3 mr-1" /> Edit Squad
                        </Button>
                        <Button size="sm" onClick={() => checkIn(t)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`checkin-btn-${t.id}`}>
                          <CheckCircle2 className="h-3 w-3 mr-1" /> Check In
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setNoShowTarget(t)} className="border-red-200 text-red-700 hover:bg-red-50" data-testid={`noshow-btn-${t.id}`}>
                          <X className="h-3 w-3 mr-1" /> No Show
                        </Button>
                      </>
                    )}
                    {isCheckedIn && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setEditTarget(t)} data-testid={`view-squad-btn-${t.id}`}>
                          {locked && !isSuperAdmin ? 'View Squad' : 'Edit Squad'}
                        </Button>
                        {isSuperAdmin && (
                          <Button size="sm" variant="ghost" onClick={() => undoCheckIn(t)} className="text-amber-700" data-testid={`undo-checkin-btn-${t.id}`}>
                            Undo
                          </Button>
                        )}
                      </>
                    )}
                    {isNoShow && (
                      <Button size="sm" variant="outline" onClick={() => undoCheckIn(t)} data-testid={`undo-noshow-btn-${t.id}`}>
                        Undo No Show
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {editTarget && (
        <EditSquadModal
          tournament={tournament}
          team={editTarget}
          isSuperAdmin={isSuperAdmin}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); onChanged(); }}
        />
      )}
      {noShowTarget && (
        <NoShowModal
          tournament={tournament}
          team={noShowTarget}
          allMatches={allMatches}
          onClose={() => setNoShowTarget(null)}
          onSaved={() => { setNoShowTarget(null); onChanged(); }}
        />
      )}
    </Card>
  );
};

const EditSquadModal = ({ tournament, team, isSuperAdmin, onClose, onSaved }) => {
  const [players, setPlayers] = useState(() =>
    (team.players || []).map(p => ({ ...p })),
  );
  const [saving, setSaving] = useState(false);
  const locked = !!team.team_sheet_locked;
  const readOnly = locked && !isSuperAdmin;
  const showDupr = tournament.dupr_enabled;
  const showGender = tournament.format === 'mixed_americano';

  const update = (idx, field, value) => {
    setPlayers(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };
  const remove = (idx) => setPlayers(prev => prev.filter((_, i) => i !== idx));
  const add = () => setPlayers(prev => [...prev, { name: '', phone: '', position: '' }]);

  const save = async () => {
    const cleaned = players.filter(p => (p.name || '').trim()).map(p => ({
      ...(p.id ? { id: p.id } : {}),
      name: p.name.trim(),
      phone: p.phone || null,
      position: p.position || null,
      dupr_id: p.dupr_id || null,
      dupr_rating: p.dupr_rating ? parseFloat(p.dupr_rating) : null,
      gender: p.gender || null,
    }));
    setSaving(true);
    try {
      await api.request(`/tournaments/admin/${tournament.id}/teams/${team.id}/players`, {
        method: 'PATCH',
        body: JSON.stringify({ players: cleaned }),
      });
      toast({ title: 'Squad updated' });
      onSaved();
    } catch (e) {
      toast({ title: 'Could not save squad', description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="edit-squad-modal">
        <DialogHeader>
          <DialogTitle>{readOnly ? 'View Squad' : 'Edit Squad'} — {team.team_name}</DialogTitle>
          <DialogDescription>{(players.length)} player{players.length !== 1 ? 's' : ''}{tournament.dupr_enabled ? ' · DUPR ratings shown' : ''}</DialogDescription>
        </DialogHeader>
        {locked && (
          <div className={`rounded-md border p-3 text-xs flex items-start gap-2 ${isSuperAdmin ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-gray-200 bg-gray-50 text-gray-700'}`} data-testid="squad-locked-banner">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              Team sheet is <strong>locked</strong> after check-in.
              {isSuperAdmin
                ? ' You are signed in as superadmin — changes allowed.'
                : ' Contact superadmin to make changes.'}
            </span>
          </div>
        )}
        <div className="space-y-2 max-h-[55vh] overflow-y-auto">
          {players.length === 0 && (
            <div className="text-xs text-gray-500 text-center py-3">No players yet.</div>
          )}
          {players.map((p, idx) => (
            <div key={p.id || idx} className="border rounded-md p-2 grid grid-cols-1 sm:grid-cols-[1.4fr_1fr_1fr_auto] gap-2" data-testid={`squad-player-row-${idx}`}>
              <input type="text" placeholder="Name *" value={p.name || ''} disabled={readOnly}
                onChange={e => update(idx, 'name', e.target.value)}
                className="px-2 py-1.5 border rounded text-sm" data-testid={`squad-name-${idx}`} />
              <input type="text" placeholder="Position" value={p.position || ''} disabled={readOnly}
                onChange={e => update(idx, 'position', e.target.value)}
                className="px-2 py-1.5 border rounded text-sm" />
              <input type="tel" placeholder="Phone" value={p.phone || ''} disabled={readOnly}
                onChange={e => update(idx, 'phone', e.target.value)}
                className="px-2 py-1.5 border rounded text-sm" />
              {!readOnly && (
                <button onClick={() => remove(idx)} className="text-red-500 px-2" aria-label="remove player" data-testid={`squad-remove-${idx}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
              {showDupr && (
                <>
                  <input type="text" placeholder="DUPR ID" value={p.dupr_id || ''} disabled={readOnly}
                    onChange={e => update(idx, 'dupr_id', e.target.value)}
                    className="px-2 py-1.5 border rounded text-sm" />
                  <input type="number" step="0.01" min="0" max="8.5" placeholder="DUPR" value={p.dupr_rating || ''} disabled={readOnly}
                    onChange={e => update(idx, 'dupr_rating', e.target.value)}
                    className="px-2 py-1.5 border rounded text-sm" />
                </>
              )}
              {showGender && (
                <select value={p.gender || ''} disabled={readOnly}
                  onChange={e => update(idx, 'gender', e.target.value || null)}
                  className="px-2 py-1.5 border rounded text-sm">
                  <option value="">Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              )}
            </div>
          ))}
        </div>
        {!readOnly && (
          <Button variant="outline" onClick={add} className="self-start" data-testid="squad-add-player-btn">
            <Plus className="h-3 w-3 mr-1" /> Add Player
          </Button>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {!readOnly && (
            <Button onClick={save} disabled={saving} data-testid="squad-save-btn">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save Changes
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const NoShowModal = ({ tournament, team, allMatches, onClose, onSaved }) => {
  // Find this team's scheduled / pending matches
  const candidate = useMemo(() => (allMatches || []).filter(m =>
    (m.team1_id === team.id || m.team2_id === team.id) &&
    m.status !== 'completed' && m.status !== 'bye',
  ), [allMatches, team.id]);
  const [selected, setSelected] = useState(() => new Set(candidate.map(m => m.id)));
  const [saving, setSaving] = useState(false);

  const toggle = (mid) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(mid)) next.delete(mid); else next.add(mid);
      return next;
    });
  };

  const opponentName = (m) => {
    const oppId = m.team1_id === team.id ? m.team2_id : m.team1_id;
    return oppId || 'TBD';
  };

  const confirm = async () => {
    setSaving(true);
    try {
      await api.request(`/tournaments/admin/${tournament.id}/teams/${team.id}/no-show`, {
        method: 'POST',
        body: JSON.stringify({ affected_match_ids: Array.from(selected) }),
      });
      toast({ title: `${team.team_name} marked as no show`, description: selected.size ? `${selected.size} walkover${selected.size !== 1 ? 's' : ''} awarded` : null });
      onSaved();
    } catch (e) {
      toast({ title: 'No-show failed', description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="noshow-modal">
        <DialogHeader>
          <DialogTitle>Mark {team.team_name} as No Show?</DialogTitle>
          <DialogDescription>
            This team will not participate. You can optionally award walkovers for their matches.
          </DialogDescription>
        </DialogHeader>
        {candidate.length === 0 ? (
          <div className="text-sm text-gray-500 py-3">No upcoming matches for this team.</div>
        ) : (
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {candidate.map(m => (
              <label key={m.id} className="flex items-center gap-2 border rounded-md px-3 py-2 text-sm cursor-pointer hover:bg-gray-50">
                <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggle(m.id)} data-testid={`noshow-match-${m.id}`} />
                <span className="text-xs text-gray-500">R{m.round} · M{m.match_number}</span>
                <span className="ml-auto">Award walkover to <strong>{opponentName(m)}</strong></span>
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={confirm} disabled={saving} className="bg-red-600 hover:bg-red-700" data-testid="noshow-confirm-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Confirm No Show
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
